import React, { useState, useEffect } from 'react'
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import ProductSearchInput from './ProductSearchInput'
import MessagesService from '../services/messagesService'
import { generatePrintableQR, downloadQRCode, getReplenishUrl } from '../services/qrCodeService'
import { validateImageUrl } from '../utils/imageValidation'

export default function DeviceDetail({ deviceId, onClose, onDeviceUpdated }) {
  const { userClaims, currentUser, userPermissions, isSuperAdmin } = useAuth()
  const [device, setDevice] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [formData, setFormData] = useState({})
  const [showServicePin, setShowServicePin] = useState(false)
  const [servicePinValue, setServicePinValue] = useState('')
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [locationValue, setLocationValue] = useState('')
  const [showLayoutEditor, setShowLayoutEditor] = useState(false)
  const [layoutData, setLayoutData] = useState({})
  const [servicePinError, setServicePinError] = useState('')
  const [editingSlot, setEditingSlot] = useState(null)
  const [imageUrlError, setImageUrlError] = useState('')
  
  // Payment Gateway Configuration States
  const [showPaymentConfig, setShowPaymentConfig] = useState(false)
  const [upiEnabled, setUpiEnabled] = useState(false)
  const [selectedUpiProvider, setSelectedUpiProvider] = useState('paytm')
  const [smartCardEnabled, setSmartCardEnabled] = useState(false)
  const [pluxeeEnabled, setPluxeeEnabled] = useState(false)
  const [pluxeeFormData, setPluxeeFormData] = useState({
    apiKey: '',
    aid: '',
    mid: '',
    tid: ''
  })
  const [paymentFormData, setPaymentFormData] = useState({
    paytm: {
      MID: '',
      MID_KEY: ''
    },
    amazon: {
      secret_key: '',
      access_key: '',
      merchantId: '',
      storeId: ''
    },
    phonepe: {}
  })
  const [paymentError, setPaymentError] = useState('')
  const [showMidKey, setShowMidKey] = useState(false)
  const [enablePurchaseButton, setEnablePurchaseButton] = useState(false)
  const [updatingPurchaseButton, setUpdatingPurchaseButton] = useState(false)
  const [expiryTrackingEnabled, setExpiryTrackingEnabled] = useState(false)
  const [updatingExpiryTracking, setUpdatingExpiryTracking] = useState(false)
  const [slotFormData, setSlotFormData] = useState({
    name: '',
    price: '',
    quantity: '',
    capacity: '',
    imageUrl: '',
    slotNumber: '',
    row: 0,
    column: 0,
    expiry: ''
  })
  const [tempLayoutData, setTempLayoutData] = useState({})
  const [layoutRows, setLayoutRows] = useState(6)
  const [layoutCols, setLayoutCols] = useState(5)
  const [rowConfigs, setRowConfigs] = useState([])
  
  // QR Code Management States
  const [showQRModal, setShowQRModal] = useState(false)
  const [qrData, setQrData] = useState(null)
  const [generatingQR, setGeneratingQR] = useState(false)

  // Customer Care Configuration States
  const [showCustomerCare, setShowCustomerCare] = useState(false)
  const [customerCareData, setCustomerCareData] = useState({
    phone: '',
    email: '',
    chat: '',
    website: ''
  })
  const [customerCareSaving, setCustomerCareSaving] = useState(false)

  useEffect(() => {
    if (deviceId && userClaims?.customer) {
      fetchDeviceDetails()
    }
  }, [deviceId, userClaims])

  const fetchDeviceDetails = async () => {
    setLoading(true)
    setError(null)

    try {
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      const snapshot = await getDoc(deviceRef)

      if (snapshot.exists()) {
        const deviceData = { id: snapshot.id, ...snapshot.data() }

        // Enforce device_restrictions for non-super-admin users
        const restrictions = userPermissions?.device_restrictions
        if (!isSuperAdmin && Array.isArray(restrictions) && restrictions.length > 0 && !restrictions.includes(deviceId)) {
          setError('access_denied')
          setLoading(false)
          return
        }

        // Ensure location field always exists in formData
        const deviceWithLocation = { ...deviceData, location: deviceData.location || '' }
        setDevice(deviceWithLocation)
        setFormData(deviceWithLocation)
        setServicePinValue(deviceData['service-pin'] || '')
        setLocationValue(deviceData.location || '')
        setEnablePurchaseButton(deviceData.enable_purchase_button || false)
        setExpiryTrackingEnabled(deviceData.expiry_tracking_enabled || false)
        setLayoutData(deviceData.slots || {})
        setTempLayoutData(deviceData.slots || {})
        setLayoutRows(deviceData.rows || 6)
        setLayoutCols(deviceData.cols || 5)
        setRowConfigs(Array.isArray(deviceData.row_configs) ? deviceData.row_configs : [])
        
        // Payment configuration is loaded when modal opens via loadExistingPaymentConfig()
        console.log('Device detail loaded:', deviceData)
      } else {
        setError('Device not found')
      }
    } catch (err) {
      console.error('Error fetching device details:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)

    try {
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      
      // Remove id and toggle-managed fields (they have their own dedicated save paths)
      const { id, expiry_tracking_enabled, enable_purchase_button, ...dataToSave } = formData
      
      await updateDoc(deviceRef, dataToSave)
      
      setDevice(formData)
      setEditMode(false)
      onDeviceUpdated && onDeviceUpdated()
      
      console.log('Device updated successfully')
    } catch (err) {
      console.error('Error updating device:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this device? This action cannot be undone.')) {
      return
    }

    setSaving(true)
    setError(null)

    try {
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      
      await deleteDoc(deviceRef)
      
      onDeviceUpdated && onDeviceUpdated()
      onClose && onClose()
      
      console.log('Device deleted successfully')
    } catch (err) {
      console.error('Error deleting device:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const validateServicePin = (pin) => {
    if (!pin) {
      return 'Service PIN is required'
    }
    if (!/^\d+$/.test(pin)) {
      return 'Service PIN must contain only numbers'
    }
    if (pin.length < 5 || pin.length > 10) {
      return 'Service PIN must be between 5 and 10 digits'
    }
    return ''
  }

  const handleServicePinSave = async () => {
    const validationError = validateServicePin(servicePinValue)
    if (validationError) {
      setServicePinError(validationError)
      return
    }

    setSaving(true)
    setError(null)
    setServicePinError('')

    try {
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      
      await updateDoc(deviceRef, {
        'service-pin': servicePinValue
      })
      
      setDevice(prev => ({ ...prev, 'service-pin': servicePinValue }))
      setShowServicePin(false)
      
      // Log activity to messages collection
      try {
        const messagesService = new MessagesService(userClaims.customer)
        await messagesService.writeMessage({
          type: 'Service PIN changed',
          deviceId: deviceId,
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          additionalData: {
            timestamp: new Date().toISOString()
          }
        })
        // Trigger dashboard refresh to show new activity
        if (onDeviceUpdated) {
          onDeviceUpdated()
        }
      } catch (logError) {
        console.error('Failed to log service PIN change activity:', logError)
        // Don't fail the operation if logging fails
      }
      
      console.log('Service PIN updated successfully')
    } catch (err) {
      console.error('Error updating service PIN:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleLocationSave = async () => {
    setSaving(true)
    setError(null)

    try {
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      
      await updateDoc(deviceRef, {
        location: locationValue
      })
      
      setDevice(prev => ({ ...prev, location: locationValue }))
      setFormData(prev => ({ ...prev, location: locationValue }))
      setShowLocationModal(false)
      
      // Log activity to messages collection
      try {
        const messagesService = new MessagesService(userClaims.customer)
        await messagesService.writeMessage({
          type: 'Location updated',
          deviceId: deviceId,
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          additionalData: {
            location: locationValue,
            timestamp: new Date().toISOString()
          }
        })
        // Note: Not calling onDeviceUpdated() to keep user in the device configuration UI
      } catch (logError) {
        console.error('Failed to log location update activity:', logError)
        // Don't fail the operation if logging fails
      }
      
      console.log('Location updated successfully')
    } catch (err) {
      console.error('Error updating location:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handlePurchaseButtonToggle = async (enabled) => {
    setUpdatingPurchaseButton(true)
    setError(null)
    
    try {
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      
      await updateDoc(deviceRef, {
        enable_purchase_button: enabled
      })
      
      // Update local state
      setEnablePurchaseButton(enabled)
      setDevice(prev => ({ ...prev, enable_purchase_button: enabled }))
      setFormData(prev => ({ ...prev, enable_purchase_button: enabled }))
      
      // Log activity to messages collection
      try {
        const messagesService = new MessagesService(userClaims.customer)
        await messagesService.writeMessage({
          type: `Purchase button ${enabled ? 'enabled' : 'disabled'}`,
          deviceId: deviceId,
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          additionalData: {
            timestamp: new Date().toISOString(),
            enabled: enabled
          }
        })
        // Note: Not calling onDeviceUpdated() to keep user in the same UI
      } catch (logError) {
        console.error('Failed to log purchase button change activity:', logError)
        // Don't fail the operation if logging fails
      }
      
      console.log('Purchase button setting updated successfully')
    } catch (err) {
      console.error('Error updating purchase button setting:', err)
      setError('Failed to update purchase button setting')
    } finally {
      setUpdatingPurchaseButton(false)
    }
  }

  const handleExpiryTrackingToggle = async (enabled) => {
    setUpdatingExpiryTracking(true)
    setError(null)
    try {
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      await updateDoc(deviceRef, { expiry_tracking_enabled: enabled })
      setExpiryTrackingEnabled(enabled)
      setDevice(prev => ({ ...prev, expiry_tracking_enabled: enabled }))
      setFormData(prev => ({ ...prev, expiry_tracking_enabled: enabled }))
    } catch (err) {
      console.error('Error toggling expiry tracking:', err)
      setError('Failed to update expiry tracking setting')
    } finally {
      setUpdatingExpiryTracking(false)
    }
  }

  // Customer Care Configuration Functions
  const loadCustomerCareConfig = () => {
    const customerCare = device?.customer_care || {}
    setCustomerCareData({
      phone: customerCare.phone || '',
      email: customerCare.email || '',
      chat: customerCare.chat || '',
      website: customerCare.website || ''
    })
  }

  const handleSaveCustomerCare = async () => {
    setCustomerCareSaving(true)
    setError(null)
    try {
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      const updatedCare = {
        phone: customerCareData.phone.trim(),
        email: customerCareData.email.trim(),
        chat: customerCareData.chat.trim(),
        website: customerCareData.website.trim()
      }
      
      await updateDoc(deviceRef, { customer_care: updatedCare })
      
      // Update local device state
      setDevice(prev => ({ ...prev, customer_care: updatedCare }))
      
      // Log the change (separate try/catch so logging errors don't fail the save)
      try {
        const messagesService = new MessagesService(userClaims.customer)
        await messagesService.writeMessage({
          type: 'Customer Care Updated',
          deviceId: deviceId,
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          additionalData: {
            timestamp: new Date().toISOString()
          }
        })
      } catch (logError) {
        console.error('Failed to log customer care update:', logError)
        // Don't fail the operation if logging fails
      }
      
      setShowCustomerCare(false)
      if (onDeviceUpdated) onDeviceUpdated()
    } catch (err) {
      console.error('Error saving customer care config:', err)
      setError('Failed to save customer care settings')
    } finally {
      setCustomerCareSaving(false)
    }
  }

  // Payment Gateway Configuration Functions
  const validatePaymentConfig = () => {
    if (selectedUpiProvider === 'paytm') {
      const { MID, MID_KEY } = paymentFormData.paytm
      if (!MID || !MID_KEY) {
        return 'Both MID and MID_KEY are required for Paytm configuration'
      }
      if (MID.length < 5) {
        return 'MID must be at least 5 characters long'
      }
      if (MID_KEY.length < 10) {
        return 'MID_KEY must be at least 10 characters long'
      }
    }
    
    if (selectedUpiProvider === 'amazon') {
      const { secret_key, access_key, merchantId, storeId } = paymentFormData.amazon
      if (!secret_key || !access_key || !merchantId || !storeId) {
        return 'All fields (Secret Key, Access Key, Merchant ID, Store ID) are required for Amazon configuration'
      }
      if (secret_key.length < 10) {
        return 'Secret Key must be at least 10 characters long'
      }
      if (access_key.length < 10) {
        return 'Access Key must be at least 10 characters long'
      }
      if (merchantId.length < 5) {
        return 'Merchant ID must be at least 5 characters long'
      }
      if (storeId.length < 3) {
        return 'Store ID must be at least 3 characters long'
      }
    }
    return ''
  }

  const handlePaymentConfigSave = async () => {
    // Only validate if UPI is enabled
    if (upiEnabled && selectedUpiProvider) {
      const validationError = validatePaymentConfig()
      if (validationError) {
        setPaymentError(validationError)
        return
      }
    }
    
    // Ensure at least one payment method is enabled
    if (!upiEnabled && !smartCardEnabled && !pluxeeEnabled) {
      setPaymentError('Please enable at least one payment method (UPI, Smart Card, or Pluxee/Sodexo)')
      return
    }

    setSaving(true)
    setError(null)
    setPaymentError('')

    try {
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      
      // Build the active array based on current selections
      const activePayments = []
      if (upiEnabled && selectedUpiProvider) {
        activePayments.push(`UPI.${selectedUpiProvider}`)
      }
      if (smartCardEnabled) {
        activePayments.push('smartcard')
      }
      if (pluxeeEnabled) {
        activePayments.push('pluxee')
      }
      
      // Build payment configuration in NEW format with active array
      // Preserve ALL UPI configs, not just the active one
      let paymentsConfig = {
        active: activePayments,
        UPI: {}
      }
      
      // Preserve existing UPI configurations from device
      const existingUPI = device?.payments?.UPI || {}
      
      // Always save all UPI configs that have data
      if (paymentFormData.paytm?.MID) {
        paymentsConfig.UPI.paytm = {
          MID: paymentFormData.paytm.MID,
          MID_KEY: paymentFormData.paytm.MID_KEY
        }
      } else if (existingUPI.paytm) {
        paymentsConfig.UPI.paytm = existingUPI.paytm
      }
      
      if (paymentFormData.amazon?.secret_key) {
        paymentsConfig.UPI.amazon = {
          secret_key: paymentFormData.amazon.secret_key,
          access_key: paymentFormData.amazon.access_key,
          merchantId: paymentFormData.amazon.merchantId,
          storeId: paymentFormData.amazon.storeId
        }
      } else if (existingUPI.amazon) {
        paymentsConfig.UPI.amazon = existingUPI.amazon
      }
      
      if (paymentFormData.phonepe && Object.keys(paymentFormData.phonepe).length > 0) {
        paymentsConfig.UPI.phonepe = paymentFormData.phonepe
      } else if (existingUPI.phonepe) {
        paymentsConfig.UPI.phonepe = existingUPI.phonepe
      }
      
      const existingPluxee = device?.payments?.pluxee || {}
      paymentsConfig.pluxee = {
        apiKey: pluxeeFormData.apiKey || existingPluxee.apiKey || '',
        aid: pluxeeFormData.aid || existingPluxee.aid || '',
        mid: pluxeeFormData.mid || existingPluxee.mid || '',
        tid: pluxeeFormData.tid || existingPluxee.tid || ''
      }
      
      await updateDoc(deviceRef, {
        payments: paymentsConfig
      })
      
      setDevice(prev => ({ ...prev, payments: paymentsConfig }))
      setShowPaymentConfig(false)
      
      // Log activity to messages collection
      try {
        const messagesService = new MessagesService(userClaims.customer)
        await messagesService.writeMessage({
          type: 'Payment configuration updated',
          deviceId: deviceId,
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          additionalData: {
            activePayments: activePayments,
            upiEnabled: upiEnabled,
            provider: upiEnabled ? selectedUpiProvider : 'none',
            smartCardEnabled: smartCardEnabled,
            pluxeeEnabled: pluxeeEnabled,
            timestamp: new Date().toISOString()
          }
        })
        // Trigger dashboard refresh to show new activity
        if (onDeviceUpdated) {
          onDeviceUpdated()
        }
      } catch (logError) {
        console.error('Failed to log payment configuration activity:', logError)
        // Don't fail the operation if logging fails
      }
      
      console.log('Payment configuration updated successfully')
    } catch (err) {
      console.error('Error updating payment configuration:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handlePaymentFormChange = (provider, field, value) => {
    setPaymentFormData(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value
      }
    }))
    setPaymentError('') // Clear error when user types
  }

  // Load existing payment configuration from device data
  // Supports both old structure (UPI directly under payments) and new structure (with active array)
  const loadExistingPaymentConfig = () => {
    const payments = device?.payments
    
    if (!payments) {
      setUpiEnabled(false)
      setSelectedUpiProvider('paytm')
      setSmartCardEnabled(false)
      setPluxeeEnabled(false)
      setPluxeeFormData({ apiKey: '', aid: '', mid: '', tid: '' })
      setPaymentError('')
      return
    }
    
    // Check for new structure with 'active' array
    const hasActiveArray = Array.isArray(payments.active)
    
    // List of supported UPI providers (phonepe is not available yet)
    const supportedProviders = ['paytm', 'amazon']
    
    // Load smart card status from active array (new structure only)
    if (hasActiveArray) {
      setSmartCardEnabled(payments.active.includes('smartcard'))
      setPluxeeEnabled(payments.active.includes('pluxee'))
      
      // Determine active UPI provider from active array
      const activeUpi = payments.active.find(item => item.startsWith('UPI.'))
      if (activeUpi) {
        const provider = activeUpi.replace('UPI.', '')
        setUpiEnabled(true)
        setSelectedUpiProvider(supportedProviders.includes(provider) ? provider : 'paytm')
      } else {
        setUpiEnabled(false)
        setSelectedUpiProvider('paytm')
      }
    } else {
      setSmartCardEnabled(false)
      setPluxeeEnabled(false)
      setUpiEnabled(false)
    }
    
    // Load UPI configurations (works for both old and new structures)
    if (payments.UPI) {
      const upiConfig = payments.UPI
      
      // Load all available UPI configs into form data
      const newFormData = {
        paytm: {
          MID: upiConfig.paytm?.MID || '',
          MID_KEY: upiConfig.paytm?.MID_KEY || ''
        },
        amazon: {
          secret_key: upiConfig.amazon?.secret_key || '',
          access_key: upiConfig.amazon?.access_key || '',
          merchantId: upiConfig.amazon?.merchantId || '',
          storeId: upiConfig.amazon?.storeId || ''
        },
        phonepe: upiConfig.phonepe || {}
      }
      setPaymentFormData(newFormData)
      
      // For old structure (no active array), determine selected provider from which has data
      // Only enable UPI for supported providers (paytm, amazon)
      if (!hasActiveArray) {
        if (upiConfig.paytm?.MID) {
          setUpiEnabled(true)
          setSelectedUpiProvider('paytm')
        } else if (upiConfig.amazon?.secret_key) {
          setUpiEnabled(true)
          setSelectedUpiProvider('amazon')
        } else if (upiConfig.phonepe && Object.keys(upiConfig.phonepe).length > 0) {
          // PhonePe is not supported, default to paytm but keep UPI enabled
          setUpiEnabled(true)
          setSelectedUpiProvider('paytm')
        } else {
          setUpiEnabled(false)
          setSelectedUpiProvider('paytm')
        }
      }
    } else {
      setSelectedUpiProvider('paytm')
    }
    
    if (payments.pluxee) {
      setPluxeeFormData({
        apiKey: payments.pluxee.apiKey || '',
        aid: payments.pluxee.aid || '',
        mid: payments.pluxee.mid || '',
        tid: payments.pluxee.tid || ''
      })
    } else {
      setPluxeeFormData({ apiKey: '', aid: '', mid: '', tid: '' })
    }
    
    // Clear any previous errors
    setPaymentError('')
  }

  const isSlotExpired = (slot) => {
    if (!slot?.expiry || !expiryTrackingEnabled) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const expiryDate = new Date(slot.expiry)
    return expiryDate < today
  }

  // Helper function to format timestamps
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = new Date(timestamp * 1000)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  // Helper function to format MAC addresses
  const formatMacAddress = (mac) => {
    if (!mac) return 'N/A'
    // Remove any existing separators and format consistently
    const cleanMac = mac.replace(/[:-]/g, '')
    return cleanMac.match(/.{2}/g)?.join(':').toUpperCase() || mac.toUpperCase()
  }

  // Helper function to copy text to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here if needed
    })
  }

  // QR Code generation and management functions
  const handleGenerateQR = async () => {
    try {
      setGeneratingQR(true)
      const qrData = await generatePrintableQR(
        deviceId,
        device?.name || `Machine ${deviceId}`,
        device?.location || 'Location not specified',
        window.location.origin
      )
      setQrData(qrData)
      setShowQRModal(true)
    } catch (error) {
      console.error('Error generating QR code:', error)
      alert('Failed to generate QR code. Please try again.')
    } finally {
      setGeneratingQR(false)
    }
  }

  const handleDownloadQR = async () => {
    try {
      await downloadQRCode(
        deviceId,
        device?.name || `Machine ${deviceId}`,
        window.location.origin
      )
    } catch (error) {
      console.error('Error downloading QR code:', error)
      alert('Failed to download QR code. Please try again.')
    }
  }

  const handleCopyReplenishUrl = () => {
    const url = getReplenishUrl(deviceId, window.location.origin)
    copyToClipboard(url)
    alert('Replenishment URL copied to clipboard!')
  }

  const handleLayoutSave = async () => {
    setSaving(true)
    setError(null)

    try {
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      
      await updateDoc(deviceRef, {
        slots: tempLayoutData,
        rows: layoutRows,
        cols: layoutCols,
        row_configs: rowConfigs,
        layout_mode: formData.layout_mode
      })
      
      setDevice(prev => ({ ...prev, slots: tempLayoutData, rows: layoutRows, cols: layoutCols, row_configs: rowConfigs, layout_mode: formData.layout_mode }))
      setLayoutData(tempLayoutData)
      setShowLayoutEditor(false)
      
      // Log activity to messages collection
      try {
        const messagesService = new MessagesService(userClaims.customer)
        await messagesService.writeMessage({
          type: 'Layout updated',
          deviceId: deviceId,
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          additionalData: {
            rows: layoutRows,
            cols: layoutCols,
            layoutMode: formData.layout_mode,
            slotCount: Object.keys(tempLayoutData).length,
            timestamp: new Date().toISOString()
          }
        })
        // Trigger dashboard refresh to show new activity
        if (onDeviceUpdated) {
          onDeviceUpdated()
        }
      } catch (logError) {
        console.error('Failed to log layout update activity:', logError)
        // Don't fail the operation if logging fails
      }
      
      console.log('Layout updated successfully')
    } catch (err) {
      console.error('Error updating layout:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const getDefaultSlotNumber = (row, col) => {
    return (row * 10) + col + 10
  }

  const handleSlotEdit = (row, col, slotId, slot) => {
    setEditingSlot({ row, col, slotId })
    setSlotFormData({
      name: slot?.name || '',
      price: slot?.price || '',
      quantity: slot?.quantity || '',
      capacity: slot?.capacity || slot?.quantity || '',
      imageUrl: slot?.imageUrl || '',
      slotNumber: slotId || getDefaultSlotNumber(row, col).toString(),
      row: row,
      column: col,
      expiry: slot?.expiry || ''
    })
  }

  const handleSlotSave = () => {
    // Validate image URL before saving
    if (imageUrlError) {
      alert('Please fix the image URL error before saving.')
      return
    }
    
    // Validate quantity and capacity are not negative
    const qty = parseInt(slotFormData.quantity) || 0
    const cap = parseInt(slotFormData.capacity) || 0
    if (qty < 0 || cap < 0) {
      alert('Quantity and capacity must be zero or greater.')
      return
    }
    
    // Validate quantity doesn't exceed capacity (when capacity is set)
    if (cap > 0 && qty > cap) {
      alert('Current quantity cannot exceed max capacity.')
      return
    }
    
    const updatedSlots = { ...tempLayoutData }
    const slotNumber = slotFormData.slotNumber || getDefaultSlotNumber(editingSlot.row, editingSlot.col).toString()
    
    if (editingSlot.slotId) {
      // Update existing slot - remove old key if slot number changed
      if (editingSlot.slotId !== slotNumber) {
        delete updatedSlots[editingSlot.slotId]
      }
      updatedSlots[slotNumber] = {
        ...updatedSlots[editingSlot.slotId],
        name: slotFormData.name,
        price: parseFloat(slotFormData.price) || 0,
        quantity: parseInt(slotFormData.quantity) || 0,
        capacity: parseInt(slotFormData.capacity) || parseInt(slotFormData.quantity) || 0,
        imageUrl: slotFormData.imageUrl,
        row: editingSlot.row,
        column: editingSlot.col,
        expiry: slotFormData.expiry || ''
      }
    } else {
      // Create new slot using slot number as key
      updatedSlots[slotNumber] = {
        name: slotFormData.name,
        price: parseFloat(slotFormData.price) || 0,
        quantity: parseInt(slotFormData.quantity) || 0,
        capacity: parseInt(slotFormData.capacity) || parseInt(slotFormData.quantity) || 0,
        imageUrl: slotFormData.imageUrl,
        row: editingSlot.row,
        column: editingSlot.col,
        expiry: slotFormData.expiry || ''
      }
    }
    
    setTempLayoutData(updatedSlots)
    setEditingSlot(null)
    setSlotFormData({ name: '', price: '', quantity: '', capacity: '', imageUrl: '', slotNumber: '', row: 0, column: 0, expiry: '' })
    setImageUrlError('')
  }

  const handleSlotDelete = () => {
    if (editingSlot.slotId) {
      const updatedSlots = { ...tempLayoutData }
      delete updatedSlots[editingSlot.slotId]
      setTempLayoutData(updatedSlots)
    }
    setEditingSlot(null)
    setSlotFormData({ name: '', price: '', quantity: '', capacity: '', imageUrl: '', slotNumber: '', row: 0, column: 0, expiry: '' })
    setImageUrlError('')
  }

  const getRowColumnCount = (row) => {
    return (Array.isArray(rowConfigs) && rowConfigs[row]) || layoutCols
  }

  const addColumnToRow = (row) => {
    const newRowConfigs = Array.isArray(rowConfigs) ? [...rowConfigs] : []
    const currentCols = getRowColumnCount(row)
    
    // Ensure array has enough elements
    while (newRowConfigs.length <= row) {
      newRowConfigs.push(layoutCols)
    }
    
    newRowConfigs[row] = currentCols + 1
    setRowConfigs(newRowConfigs)
  }

  const removeColumnFromRow = (row) => {
    const currentCols = getRowColumnCount(row)
    if (currentCols > 1) {
      const newRowConfigs = Array.isArray(rowConfigs) ? [...rowConfigs] : []
      
      // Ensure array has enough elements
      while (newRowConfigs.length <= row) {
        newRowConfigs.push(layoutCols)
      }
      
      newRowConfigs[row] = currentCols - 1
      
      // Remove slots from the last column of this row
      const updatedSlots = { ...tempLayoutData }
      Object.keys(updatedSlots).forEach(key => {
        if (updatedSlots[key].row === row && updatedSlots[key].column === currentCols - 1) {
          delete updatedSlots[key]
        }
      })
      
      setTempLayoutData(updatedSlots)
      setRowConfigs(newRowConfigs)
    }
  }

  const addColumn = () => {
    setLayoutCols(prev => prev + 1)
  }

  const removeColumn = () => {
    if (layoutCols > 1) {
      // Remove slots from the last column across all rows
      const updatedSlots = { ...tempLayoutData }
      Object.keys(updatedSlots).forEach(key => {
        if (updatedSlots[key].column === layoutCols - 1) {
          delete updatedSlots[key]
        }
      })
      setTempLayoutData(updatedSlots)
      setLayoutCols(prev => prev - 1)
    }
  }

  const addRow = () => {
    setLayoutRows(prev => prev + 1)
  }

  const removeRow = () => {
    if (layoutRows > 1) {
      // Remove slots from the last row
      const updatedSlots = { ...tempLayoutData }
      Object.keys(updatedSlots).forEach(key => {
        if (updatedSlots[key].row === layoutRows - 1) {
          delete updatedSlots[key]
        }
      })
      setTempLayoutData(updatedSlots)
      setLayoutRows(prev => prev - 1)
      
      // Remove row config if it exists
      const newRowConfigs = Array.isArray(rowConfigs) ? [...rowConfigs] : []
      if (newRowConfigs.length >= layoutRows) {
        newRowConfigs.pop()
      }
      setRowConfigs(newRowConfigs)
    }
  }

  // Clear all slot lockouts
  const clearAllSlotLockouts = async () => {
    if (!window.confirm('Are you sure you want to clear all slot lockouts? This will make all slots functional again.')) {
      return
    }

    setSaving(true)
    setError(null)

    try {
      const updatedSlots = { ...tempLayoutData }
      let clearedCount = 0
      
      // Clear lockout status for all slots
      Object.keys(updatedSlots).forEach(key => {
        const slot = updatedSlots[key]
        if (slot.slot_lockout === true || slot.slock_lockout === true) {
          clearedCount++
        }
        // Clear both field variations to ensure compatibility
        updatedSlots[key] = {
          ...slot,
          slot_lockout: false,
          slock_lockout: false
        }
      })

      if (clearedCount === 0) {
        alert('No locked slots found to clear.')
        return
      }

      // Update database
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      
      await updateDoc(deviceRef, {
        slots: updatedSlots
      })
      
      // Update local state
      setTempLayoutData(updatedSlots)
      setDevice(prev => ({ ...prev, slots: updatedSlots }))
      setLayoutData(updatedSlots)
      
      // Log activity to messages collection
      try {
        const messagesService = new MessagesService(userClaims.customer)
        await messagesService.writeMessage({
          type: 'Slot lockouts cleared',
          deviceId: deviceId,
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          additionalData: {
            clearedCount: clearedCount,
            timestamp: new Date().toISOString()
          }
        })
        // Trigger dashboard refresh to show new activity
        if (onDeviceUpdated) {
          onDeviceUpdated()
        }
      } catch (logError) {
        console.error('Failed to log slot lockouts clearing activity:', logError)
        // Don't fail the operation if logging fails
      }
      
      alert(`Successfully cleared lockouts for ${clearedCount} slots.`)
      console.log('All slot lockouts cleared successfully')
    } catch (err) {
      console.error('Error clearing slot lockouts:', err)
      setError(err.message)
      alert('Failed to clear slot lockouts. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  // Restock all slots to their capacity
  const restockAllSlots = async () => {
    const slots = tempLayoutData
    const eligibleSlots = Object.entries(slots).filter(([key, slot]) => {
      const isLockedOut = slot.slot_lockout === true || slot.slock_lockout === true
      return !isLockedOut && slot.capacity > 0 && slot.quantity < slot.capacity
    })

    if (eligibleSlots.length === 0) {
      alert('No slots available for restocking. All functional slots are either at capacity or have no capacity set.')
      return
    }

    const restockSummary = eligibleSlots.map(([key, slot]) => 
      `Slot ${key}: ${slot.quantity} → ${slot.capacity} (+${slot.capacity - slot.quantity})`
    ).join('\n')

    if (!window.confirm(`Restock ${eligibleSlots.length} slots to their full capacity?\n\nSlots to restock:\n${restockSummary}\n\nLocked-out slots will be skipped.`)) {
      return
    }

    setSaving(true)
    setError(null)

    try {
      const updatedSlots = { ...tempLayoutData }
      let restockedCount = 0
      let totalAddedItems = 0
      
      // Restock all eligible slots
      eligibleSlots.forEach(([key, slot]) => {
        const itemsAdded = slot.capacity - slot.quantity
        updatedSlots[key] = {
          ...slot,
          quantity: slot.capacity
        }
        restockedCount++
        totalAddedItems += itemsAdded
      })

      // Update database
      const collectionName = `${userClaims.customer}_machines`
      const deviceRef = doc(db, collectionName, deviceId)
      
      await updateDoc(deviceRef, {
        slots: updatedSlots
      })
      
      // Update local state
      setTempLayoutData(updatedSlots)
      setDevice(prev => ({ ...prev, slots: updatedSlots }))
      setLayoutData(updatedSlots)
      
      // Log activity to messages collection
      try {
        const messagesService = new MessagesService(userClaims.customer)
        await messagesService.writeMessage({
          type: 'Inventory restocked',
          deviceId: deviceId,
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          additionalData: {
            restockedCount: restockedCount,
            totalAddedItems: totalAddedItems,
            timestamp: new Date().toISOString()
          }
        })
        // Trigger dashboard refresh to show new activity
        if (onDeviceUpdated) {
          onDeviceUpdated()
        }
      } catch (logError) {
        console.error('Failed to log inventory restock activity:', logError)
        // Don't fail the operation if logging fails
      }
      
      alert(`Successfully restocked ${restockedCount} slots with ${totalAddedItems} total items.`)
      console.log('All slots restocked successfully')
    } catch (err) {
      console.error('Error restocking slots:', err)
      setError(err.message)
      alert('Failed to restock slots. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const renderLayoutMatrix = () => {
    const slots = tempLayoutData
    const rows = layoutRows
    
    const matrix = []
    for (let row = 0; row < rows; row++) {
      const rowCols = getRowColumnCount(row)
      const rowSlots = []
      
      // Add row controls
      rowSlots.push(
        <div key={`row-${row}-controls`} className="d-flex flex-column align-items-center justify-content-center p-2 bg-light border" style={{ minWidth: '60px' }}>
          <small className="text-muted mb-1">Row {row + 1}</small>
          {device?.maintenance_mode && (
            <div className="btn-group-vertical btn-group-sm">
              <button 
                className="btn btn-outline-success btn-sm" 
                onClick={() => addColumnToRow(row)}
                title="Add column to this row"
              >
                <i className="fas fa-plus"></i>
              </button>
              <button 
                className="btn btn-outline-danger btn-sm" 
                onClick={() => removeColumnFromRow(row)}
                disabled={rowCols <= 1}
                title="Remove column from this row"
              >
                <i className="fas fa-minus"></i>
              </button>
            </div>
          )}
          <small className="text-muted mt-1">{rowCols} cols</small>
        </div>
      )
      
      // Add slot columns for this row
      for (let col = 0; col < rowCols; col++) {
        // Find slot for this position
        const slotEntry = Object.entries(slots).find(([key, slot]) => 
          slot.row === row && slot.column === col
        )
        
        const slot = slotEntry ? slotEntry[1] : null
        const slotId = slotEntry ? slotEntry[0] : null
        
        // Check if slot is locked out (handle both field name variations)
        const isLockedOut = slot && (slot.slot_lockout === true || slot.slock_lockout === true)
        const isExpired = isSlotExpired(slot)
        
        rowSlots.push(
          <div 
            key={`${row}-${col}`} 
            className={`border p-2 text-center position-relative ${isLockedOut ? 'slot-locked-out' : ''}`}
            style={{ 
              minHeight: '120px', 
              cursor: device?.maintenance_mode ? 'pointer' : 'default', 
              width: '120px',
              opacity: device?.maintenance_mode ? 1 : 0.8,
              borderColor: isLockedOut ? '#dc3545' : isExpired ? '#fd7e14' : undefined,
              borderWidth: (isLockedOut || isExpired) ? '2px' : undefined,
              background: isLockedOut ? 'rgba(220, 53, 69, 0.1)' : isExpired ? 'rgba(253, 126, 20, 0.1)' : undefined
            }}
            onClick={device?.maintenance_mode ? () => handleSlotEdit(row, col, slotId, slot) : undefined}
            title={isLockedOut ? 'This slot is locked out (non-functional)' : undefined}
          >
            {slot ? (
              <div>
                <small className="text-muted fw-bold">#{slotId}</small>
                {isLockedOut && (
                  <div className="position-absolute top-0 start-0 p-1">
                    <i className="fas fa-lock text-danger" style={{ fontSize: '12px' }} title="Slot locked out"></i>
                  </div>
                )}
                {isExpired && expiryTrackingEnabled && (
                  <div className="position-absolute top-0 end-0 p-1" style={{ zIndex: 1 }}>
                    <i className="fas fa-clock text-warning" style={{ fontSize: '12px' }} title={`Expired: ${slot.expiry}`}></i>
                  </div>
                )}
                {slot.imageUrl && (
                  <div className="mb-1" style={{ opacity: isLockedOut ? 0.5 : 1 }}>
                    <img 
                      src={slot.imageUrl} 
                      alt={slot.name}
                      style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                      className="rounded"
                    />
                  </div>
                )}
                <div className={`fw-bold small ${isLockedOut ? 'text-muted' : ''}`} style={{ fontSize: '11px' }}>{slot.name}</div>
                <div className={`small ${isLockedOut ? 'text-muted' : 'text-primary'}`}>₹{slot.price}</div>
                <div className="text-muted small">Qty: {slot.quantity}</div>
                {expiryTrackingEnabled && slot.expiry && (
                  <div className={`small ${isExpired ? 'text-warning fw-bold' : 'text-muted'}`} style={{ fontSize: '10px' }}>
                    {isExpired ? 'EXPIRED' : `Exp: ${slot.expiry}`}
                  </div>
                )}
                {isLockedOut && (
                  <div className="small text-danger fw-bold" style={{ fontSize: '10px' }}>LOCKED</div>
                )}
                {device?.maintenance_mode && (
                  <div className="position-absolute top-0 end-0 p-1">
                    <i className="fas fa-edit text-primary" style={{ fontSize: '10px' }}></i>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted d-flex align-items-center justify-content-center h-100">
                <div>
                  {device?.maintenance_mode ? (
                    <>
                      <i className="fas fa-plus-circle mb-1"></i>
                      <div className="small">Add Product</div>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-square mb-1"></i>
                      <div className="small">Empty Slot</div>
                    </>
                  )}
                  <div className="small text-muted">#{getDefaultSlotNumber(row, col)}</div>
                </div>
              </div>
            )}
          </div>
        )
      }
      
      matrix.push(
        <div key={row} className="d-flex mb-2">
          {rowSlots}
        </div>
      )
    }
    
    return matrix
  }

  // Currency options for the dropdown
  const currencyOptions = [
    { code: 'USD', name: 'US Dollar ($)' },
    { code: 'EUR', name: 'Euro (€)' },
    { code: 'GBP', name: 'British Pound (£)' },
    { code: 'JPY', name: 'Japanese Yen (¥)' },
    { code: 'CNY', name: 'Chinese Yuan (¥)' },
    { code: 'INR', name: 'Indian Rupee (₹)' },
    { code: 'AUD', name: 'Australian Dollar (A$)' },
    { code: 'CAD', name: 'Canadian Dollar (C$)' },
    { code: 'CHF', name: 'Swiss Franc (Fr.)' },
    { code: 'SGD', name: 'Singapore Dollar (S$)' },
    { code: 'HKD', name: 'Hong Kong Dollar (HK$)' },
    { code: 'SEK', name: 'Swedish Krona (kr)' },
    { code: 'NOK', name: 'Norwegian Krone (kr)' },
    { code: 'DKK', name: 'Danish Krone (kr)' },
    { code: 'PLN', name: 'Polish Zloty (zł)' },
    { code: 'CZK', name: 'Czech Koruna (Kč)' },
    { code: 'HUF', name: 'Hungarian Forint (Ft)' },
    { code: 'RUB', name: 'Russian Ruble (₽)' },
    { code: 'BRL', name: 'Brazilian Real (R$)' },
    { code: 'MXN', name: 'Mexican Peso ($)' },
    { code: 'KRW', name: 'South Korean Won (₩)' },
    { code: 'THB', name: 'Thai Baht (฿)' },
    { code: 'MYR', name: 'Malaysian Ringgit (RM)' },
    { code: 'IDR', name: 'Indonesian Rupiah (Rp)' },
    { code: 'PHP', name: 'Philippine Peso (₱)' },
    { code: 'VND', name: 'Vietnamese Dong (₫)' },
    { code: 'AED', name: 'UAE Dirham (د.إ)' },
    { code: 'SAR', name: 'Saudi Riyal (﷼)' },
    { code: 'ZAR', name: 'South African Rand (R)' },
    { code: 'EGP', name: 'Egyptian Pound (£)' },
    { code: 'TRY', name: 'Turkish Lira (₺)' },
    { code: 'ILS', name: 'Israeli Shekel (₪)' }
  ]

  const renderDeviceField = (key, value) => {
    // Skip rendering certain internal fields
    if (key === 'id') return null

    // Check if this is an MQTT field and user doesn't have sadmin access
    if (key.includes('_mqtt') && !userClaims?.sadmin) {
      return null
    }

    const fieldType = typeof value
    
    if (editMode) {
      // Special handling for location field
      if (key === 'location') {
        return (
          <div className="mb-3" key={key}>
            <label className="form-label">
              <i className="fas fa-map-marker-alt me-2"></i>
              Location
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter device location (optional)"
              value={formData[key] || ''}
              onChange={(e) => handleInputChange(key, e.target.value)}
            />
            <small className="form-text text-muted">
              Optional field to specify where this device is located
            </small>
          </div>
        )
      } else if (key === 'currency') {
        return (
          <div className="mb-3" key={key}>
            <label className="form-label">Currency</label>
            <select
              className="form-select"
              value={formData[key] || 'USD'}
              onChange={(e) => handleInputChange(key, e.target.value)}
            >
              {currencyOptions.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.name}
                </option>
              ))}
            </select>
          </div>
        )
      } else if (key === 'layout_mode') {
        return (
          <div className="mb-3" key={key}>
            <label className="form-label">Layout Mode</label>
            <div className="mt-2">
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="layout_mode"
                  id="layout_mode_image"
                  value="image"
                  checked={formData[key] === 'image'}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                />
                <label className="form-check-label" htmlFor="layout_mode_image">
                  <i className="fas fa-image me-1"></i>
                  Image Mode
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="layout_mode"
                  id="layout_mode_slot"
                  value="slot"
                  checked={formData[key] === 'slot'}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                />
                <label className="form-check-label" htmlFor="layout_mode_slot">
                  <i className="fas fa-th-large me-1"></i>
                  Slot Mode
                </label>
              </div>
            </div>
            <small className="form-text text-muted">
              Image mode displays product images in layout, slot mode shows slot numbers only
            </small>
          </div>
        )
      } else if (fieldType === 'boolean') {
        return (
          <div className="mb-3" key={key}>
            <label className="form-label">{key}</label>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={formData[key] || false}
                onChange={(e) => handleInputChange(key, e.target.checked)}
              />
              <label className="form-check-label">
                {formData[key] ? 'True' : 'False'}
              </label>
            </div>
          </div>
        )
      } else if (fieldType === 'number') {
        return (
          <div className="mb-3" key={key}>
            <label className="form-label">{key}</label>
            <input
              type="number"
              className="form-control"
              value={formData[key] || ''}
              onChange={(e) => handleInputChange(key, parseFloat(e.target.value) || 0)}
            />
          </div>
        )
      } else if (fieldType === 'object' && value !== null) {
        return (
          <div className="mb-3" key={key}>
            <label className="form-label">
              {key} (JSON)
              {key.includes('_mqtt') && (
                <span className="badge bg-warning text-dark ms-2">
                  <i className="fas fa-shield-alt me-1"></i>
                  Admin Only
                </span>
              )}
            </label>
            <textarea
              className="form-control"
              rows="3"
              value={JSON.stringify(formData[key], null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value)
                  handleInputChange(key, parsed)
                } catch (err) {
                  // Keep the text as is if it's not valid JSON
                }
              }}
            />
          </div>
        )
      } else {
        return (
          <div className="mb-3" key={key}>
            <label className="form-label">
              {key}
              {key.includes('_mqtt') && (
                <span className="badge bg-warning text-dark ms-2">
                  <i className="fas fa-shield-alt me-1"></i>
                  Admin Only
                </span>
              )}
            </label>
            <input
              type="text"
              className="form-control"
              value={formData[key] || ''}
              onChange={(e) => handleInputChange(key, e.target.value)}
            />
          </div>
        )
      }
    } else {
      // Display mode
      return (
        <tr key={key}>
          <td className="fw-semibold">
            {key}
            {key.includes('_mqtt') && (
              <span className="badge bg-warning text-dark ms-2">
                <i className="fas fa-shield-alt me-1"></i>
                Admin Only
              </span>
            )}
          </td>
          <td>
            {key === 'location' ? (
              <span>
                <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                {value || 'Not specified'}
              </span>
            ) : key === 'currency' ? (
              <span className="badge bg-info">
                {currencyOptions.find(c => c.code === value)?.name || value}
              </span>
            ) : key === 'layout_mode' ? (
              <span className={`badge ${value === 'image' ? 'bg-success' : 'bg-primary'}`}>
                <i className={`fas fa-${value === 'image' ? 'image' : 'th-large'} me-1`}></i>
                {value === 'image' ? 'Image Mode' : 'Slot Mode'}
              </span>
            ) : fieldType === 'boolean' ? (
              <span className={`badge ${value ? 'bg-success' : 'bg-secondary'}`}>
                {value ? 'True' : 'False'}
              </span>
            ) : fieldType === 'object' && value !== null ? (
              <pre className="small mb-0">{JSON.stringify(value, null, 2)}</pre>
            ) : (
              String(value || 'N/A')
            )}
          </td>
        </tr>
      )
    }
  }

  if (loading) {
    return (
      <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-body text-center py-5">
              <div className="spinner-border mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div>Loading device details...</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    const isAccessDenied = error === 'access_denied'
    return (
      <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{isAccessDenied ? 'Access Denied' : 'Error'}</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              {isAccessDenied ? (
                <div className="alert alert-warning">
                  <i className="fas fa-lock me-2"></i>
                  You don't have permission to view this device.
                </div>
              ) : (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Device Configuration: {device?.id}
              {device?.machine_id && (
                <span className="badge bg-secondary ms-2">{device.machine_id}</span>
              )}
              {device?.maintenance_mode && (
                <span className="badge bg-warning text-dark ms-2">
                  <i className="fas fa-wrench me-1"></i>
                  Maintenance Mode
                </span>
              )}
              {(device?.payments?.active?.length > 0 || 
                device?.payments?.smartcard === true ||
                device?.payments?.UPI?.paytm?.MID || 
                device?.payments?.UPI?.amazon?.secret_key) && (
                <i 
                  className="fas fa-wifi text-success ms-2" 
                  title="Contactless payments enabled"
                  style={{ fontSize: '1rem', transform: 'rotate(90deg)' }}
                ></i>
              )}
              <span 
                className="badge bg-primary ms-2" 
                style={{ cursor: 'pointer' }}
                onClick={() => setShowLocationModal(true)}
                title="Click to edit location"
              >
                <i className="fas fa-map-marker-alt me-1"></i>
                {device?.location || 'Set Location'}
              </span>
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* Payment Bypass Warning */}
            {device?.enable_purchase_button === true && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <div className="d-flex align-items-start">
                  <div className="me-3">
                    <i className="fas fa-unlock-alt fa-2x"></i>
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="alert-heading mb-2">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Payment Bypass is Currently Enabled
                    </h5>
                    <p className="mb-2">
                      This device is currently allowing <strong>free purchases</strong> without payment validation. 
                      Users can get products without paying.
                    </p>
                    <hr className="my-2" />
                    <p className="mb-0 small">
                      <i className="fas fa-info-circle me-1"></i>
                      This setting is typically used for testing only. Remember to disable it when deploying to production.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Device Information Section */}
            {device && device._metadata && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-primary">
                    <div style={{ 
                      backgroundColor: '#0d6efd', 
                      color: 'white',
                      padding: '0.75rem 1.25rem',
                      borderBottom: '1px solid rgba(0,0,0,.125)',
                      borderTopLeftRadius: 'calc(0.375rem - 1px)',
                      borderTopRightRadius: 'calc(0.375rem - 1px)'
                    }}>
                      <h6 style={{ 
                        margin: 0, 
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '1rem'
                      }}>
                        <i className="fas fa-info-circle" style={{ marginRight: '8px', color: 'white' }}></i>
                        Device Information
                      </h6>
                    </div>
                    <div className="card-body text-dark">
                      <div className="row">
                        {/* Network Configuration */}
                        <div className="col-lg-6 mb-3">
                          <h6 className="text-primary mb-3">
                            <i className="fas fa-network-wired me-2"></i>
                            Network Configuration
                          </h6>
                          
                          {/* WiFi Information */}
                          {device._metadata.wifi && (
                            <div className="mb-2 p-2 bg-light rounded">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-wifi me-2 text-success"></i>
                                  <strong className="text-dark">WiFi</strong>
                                  {device._metadata.wifi.ipv4 && (
                                    <span className="badge bg-success ms-2">Connected</span>
                                  )}
                                </div>
                                {device._metadata.wifi.ipv4 && (
                                  <button 
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => copyToClipboard(device._metadata.wifi.ipv4)}
                                    title="Copy IP Address"
                                  >
                                    <i className="fas fa-copy"></i>
                                  </button>
                                )}
                              </div>
                              <div className="small text-dark">
                                <div className="mb-1">
                                  <strong className="text-dark">IP:</strong> <span className="text-dark">{device._metadata.wifi.ipv4 || 'Not Connected'}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-dark">
                                    <strong className="text-dark">MAC:</strong> {formatMacAddress(device._metadata.wifi.mac)}
                                  </span>
                                  {device._metadata.wifi.mac && (
                                    <button 
                                      className="btn btn-outline-secondary btn-sm"
                                      onClick={() => copyToClipboard(device._metadata.wifi.mac)}
                                      title="Copy MAC Address"
                                    >
                                      <i className="fas fa-copy"></i>
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Ethernet Information */}
                          {device._metadata.ethernet && (
                            <div className="mb-2 p-2 bg-light rounded">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-ethernet me-2 text-warning"></i>
                                  <strong className="text-dark">Ethernet</strong>
                                  {device._metadata.ethernet.ipv4 ? (
                                    <span className="badge bg-success ms-2">Connected</span>
                                  ) : (
                                    <span className="badge bg-secondary ms-2">Not Connected</span>
                                  )}
                                </div>
                                {device._metadata.ethernet.ipv4 && (
                                  <button 
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => copyToClipboard(device._metadata.ethernet.ipv4)}
                                    title="Copy IP Address"
                                  >
                                    <i className="fas fa-copy"></i>
                                  </button>
                                )}
                              </div>
                              <div className="small text-dark">
                                <div className="mb-1">
                                  <strong className="text-dark">IP:</strong> <span className="text-dark">{device._metadata.ethernet.ipv4 || 'Not Connected'}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-dark">
                                    <strong className="text-dark">MAC:</strong> {formatMacAddress(device._metadata.ethernet.mac)}
                                  </span>
                                  {device._metadata.ethernet.mac && (
                                    <button 
                                      className="btn btn-outline-secondary btn-sm"
                                      onClick={() => copyToClipboard(device._metadata.ethernet.mac)}
                                      title="Copy MAC Address"
                                    >
                                      <i className="fas fa-copy"></i>
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* System Information */}
                        <div className="col-lg-6 mb-3">
                          <h6 className="text-primary mb-3">
                            <i className="fas fa-microchip me-2"></i>
                            System Information
                          </h6>
                          
                          <div className="mb-2 p-2 bg-light rounded">
                            <div className="small text-dark">
                              <div className="mb-2 d-flex justify-content-between align-items-center">
                                <span className="text-dark">
                                  <i className="fas fa-mobile-alt me-2 text-info"></i>
                                  <strong className="text-dark">App Version:</strong>
                                </span>
                                <span className="badge bg-info">
                                  {device._metadata.app_version || 'N/A'}
                                </span>
                              </div>
                              
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="text-dark">
                                  <i className="fas fa-clock me-2 text-success"></i>
                                  <strong className="text-dark">Last Updated:</strong>
                                </span>
                                <small className="text-muted">
                                  {formatTimestamp(device._metadata.last_updated)}
                                </small>
                              </div>
                            </div>
                          </div>

                          {/* Connection Status Overview */}
                          <div className="mt-3 p-2 border rounded">
                            <h6 className="small text-muted mb-2">
                              <i className="fas fa-signal me-1"></i>
                              Connection Status
                            </h6>
                            <div className="d-flex flex-wrap gap-2">
                              <span className={`badge ${device._metadata.wifi?.ipv4 ? 'bg-success' : 'bg-secondary'}`}>
                                <i className={`fas fa-wifi me-1`}></i>
                                WiFi {device._metadata.wifi?.ipv4 ? 'Connected' : 'Disconnected'}
                              </span>
                              <span className={`badge ${device._metadata.ethernet?.ipv4 ? 'bg-success' : 'bg-secondary'}`}>
                                <i className="fas fa-ethernet me-1"></i>
                                Ethernet {device._metadata.ethernet?.ipv4 ? 'Connected' : 'Disconnected'}
                              </span>
                            </div>
                          </div>

                          {/* Hardware Configuration */}
                          {device.hardware_config && (
                            <div className="mt-3 p-2 border rounded">
                              <h6 className="small text-muted mb-2">
                                <i className="fas fa-cogs me-1"></i>
                                Hardware Configuration
                              </h6>
                              <div className="small">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <span className="text-dark"><strong>Make:</strong></span>
                                  <span className="text-dark">{device.hardware_config.make || 'N/A'}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <span className="text-dark"><strong>Board:</strong></span>
                                  <span className="text-dark">{device.hardware_config.board || 'N/A'}</span>
                                </div>
                                {device.hardware_config.make === 'Vendviitor' && device.hardware_config.vendviitor && (
                                  <>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <span className="text-dark"><strong>Drop Sensor:</strong></span>
                                      <span className={`badge ${device.hardware_config.vendviitor.drop_sensor_enabled ? 'bg-success' : 'bg-danger'}`}>
                                        {device.hardware_config.vendviitor.drop_sensor_enabled ? 'Enabled' : 'Disabled'}
                                      </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="text-dark"><strong>Drop Mode:</strong></span>
                                      <span className="text-dark">
                                        {device.hardware_config.vendviitor.drop_sensor_mode === 0 ? 'Detection Disabled'
                                          : device.hardware_config.vendviitor.drop_sensor_mode === 1 ? 'Positional Stop'
                                          : device.hardware_config.vendviitor.drop_sensor_mode === 2 ? 'Stop on Drop'
                                          : 'Unknown'}
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => setShowServicePin(true)}
                  >
                    <i className="fas fa-key me-2"></i>
                    Configure Service PIN
                  </button>
                  <button
                    className="btn btn-outline-success"
                    onClick={() => {
                      loadExistingPaymentConfig()
                      setShowPaymentConfig(true)
                    }}
                  >
                    <i className="fas fa-credit-card me-2"></i>
                    Payment Gateway
                  </button>
                  <button
                    className="btn btn-outline-warning"
                    onClick={handleGenerateQR}
                    disabled={generatingQR}
                  >
                    {generatingQR ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        Generating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-qrcode me-2"></i>
                        QR Code
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      loadCustomerCareConfig()
                      setShowCustomerCare(true)
                    }}
                  >
                    <i className="fas fa-headset me-2"></i>
                    Customer Care
                    <span className="badge bg-danger ms-2" style={{ fontSize: '0.65rem' }}>New</span>
                  </button>
                  <button
                    className="btn btn-outline-info"
                    onClick={() => setShowLayoutEditor(true)}
                  >
                    <i className="fas fa-th-large me-2"></i>
                    {device?.maintenance_mode ? 'Edit Layout' : 'View Layout'}
                  </button>
                </div>
                
                {/* Testing Mode Toggle - Payment Bypass */}
                <div className={`mt-3 p-3 border rounded ${enablePurchaseButton ? 'bg-danger bg-opacity-10 border-danger' : 'bg-light'}`}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1">
                        <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                        Testing Mode - Payment Bypass
                        {enablePurchaseButton && <span className="badge bg-danger ms-2">LIVE RISK</span>}
                      </h6>
                      <small className={enablePurchaseButton ? 'text-danger fw-bold' : 'text-muted'}>
                        {enablePurchaseButton 
                          ? '⚠️ CRITICAL: Customers can complete purchases WITHOUT payment!' 
                          : 'Payment is required for all purchases (normal operation)'
                        }
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {updatingPurchaseButton && (
                        <i className="fas fa-spinner fa-spin text-primary"></i>
                      )}
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="purchaseToggle"
                          checked={enablePurchaseButton}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Show confirmation for enabling
                              if (window.confirm(
                                '⚠️ SECURITY WARNING ⚠️\n\n' +
                                'Enabling Testing Mode will allow customers to complete purchases WITHOUT payment!\n\n' +
                                'This should ONLY be used for testing purposes.\n' +
                                'Are you sure you want to enable this dangerous feature?'
                              )) {
                                handlePurchaseButtonToggle(true)
                              }
                            } else {
                              handlePurchaseButtonToggle(false)
                            }
                          }}
                          disabled={updatingPurchaseButton}
                          style={{ transform: 'scale(1.2)' }}
                        />
                        <label className="form-check-label fw-bold" htmlFor="purchaseToggle">
                          {enablePurchaseButton ? (
                            <span className="text-danger">
                              <i className="fas fa-exclamation-triangle me-1"></i>TESTING ON
                            </span>
                          ) : (
                            <span className="text-success">
                              <i className="fas fa-shield-alt me-1"></i>SECURE
                            </span>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                  {enablePurchaseButton && (
                    <div className="alert alert-danger mt-3 mb-0">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        <div>
                          <strong>PAYMENT BYPASS ACTIVE!</strong>
                          <div className="small">
                            This device is in testing mode. Customers can get products without paying. 
                            Disable this immediately in live locations to prevent revenue loss.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className={`mt-3 p-3 border rounded ${expiryTrackingEnabled ? 'bg-warning bg-opacity-10 border-warning' : 'bg-light'}`}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1">
                        <i className="fas fa-calendar-times me-2 text-warning"></i>
                        Expiry Date Tracking
                      </h6>
                      <small className="text-muted">
                        {expiryTrackingEnabled 
                          ? 'Expiry dates are visible on all layout views and editable in slot settings.'
                          : 'Enable to track product expiry dates for each slot.'}
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {updatingExpiryTracking && (
                        <i className="fas fa-spinner fa-spin text-primary"></i>
                      )}
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="expiryTrackingToggle"
                          checked={expiryTrackingEnabled}
                          onChange={(e) => handleExpiryTrackingToggle(e.target.checked)}
                          disabled={updatingExpiryTracking}
                          style={{ transform: 'scale(1.2)' }}
                        />
                        <label className="form-check-label fw-bold" htmlFor="expiryTrackingToggle">
                          {expiryTrackingEnabled ? (
                            <span className="text-warning">ON</span>
                          ) : (
                            <span className="text-muted">OFF</span>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                {!device?.maintenance_mode && (
                  <small className="text-muted mt-2 d-block">
                    <i className="fas fa-info-circle me-1"></i>
                    Layout editing requires maintenance mode - you can view the layout anytime
                  </small>
                )}
              </div>
            </div>

            {/* Service PIN Configuration Modal */}
            {showServicePin && (
              <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                <div className="modal-dialog">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Configure Service PIN</h5>
                      <button type="button" className="btn-close" onClick={() => setShowServicePin(false)}></button>
                    </div>
                    <div className="modal-body">
                      <div className="mb-3">
                        <label className="form-label">Service PIN (5-10 digits)</label>
                        <input
                          type="text"
                          className={`form-control ${servicePinError ? 'is-invalid' : ''}`}
                          value={servicePinValue}
                          onChange={(e) => {
                            setServicePinValue(e.target.value)
                            setServicePinError('')
                          }}
                          placeholder="Enter numeric PIN"
                          maxLength="10"
                        />
                        {servicePinError && (
                          <div className="invalid-feedback">
                            {servicePinError}
                          </div>
                        )}
                      </div>
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        Service PIN must be between 5 and 10 digits and contain only numbers.
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowServicePin(false)}>
                        Cancel
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-primary"
                        onClick={handleServicePinSave}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-2"></i>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-2"></i>
                            Save PIN
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Location Configuration Modal */}
            {showLocationModal && (
              <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                <div className="modal-dialog">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        <i className="fas fa-map-marker-alt me-2"></i>
                        Set Device Location
                      </h5>
                      <button type="button" className="btn-close" onClick={() => setShowLocationModal(false)}></button>
                    </div>
                    <div className="modal-body">
                      <div className="mb-3">
                        <label className="form-label">Location</label>
                        <input
                          type="text"
                          className="form-control"
                          value={locationValue}
                          onChange={(e) => setLocationValue(e.target.value)}
                          placeholder="Enter device location (e.g., Building A, Floor 3, Cafeteria)"
                          maxLength="100"
                        />
                        <small className="form-text text-muted">
                          {locationValue.length}/100 characters
                        </small>
                      </div>
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        Specify where this device is physically located. This helps identify and manage devices across different locations.
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowLocationModal(false)}>
                        Cancel
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-primary"
                        onClick={handleLocationSave}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-2"></i>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-2"></i>
                            Save Location
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Gateway Configuration Modal */}
            {showPaymentConfig && (
              <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        <i className="fas fa-credit-card me-2"></i>
                        Payment Gateway Configuration
                      </h5>
                      <button type="button" className="btn-close" onClick={() => setShowPaymentConfig(false)}></button>
                    </div>
                    <div className="modal-body">
                      {!device?.maintenance_mode && (
                        <div className="alert alert-warning d-flex align-items-center mb-4">
                          <i className="fas fa-lock me-2"></i>
                          <span>Enable <strong>Maintenance Mode</strong> on this device to edit payment settings.</span>
                        </div>
                      )}
                      {/* UPI Payments Section */}
                      <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">
                            <i className="fas fa-mobile-alt me-2"></i>
                            UPI Payments
                          </h6>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="upiToggle"
                              checked={upiEnabled}
                              onChange={(e) => setUpiEnabled(e.target.checked)}
                              disabled={!device?.maintenance_mode}
                              style={{ width: '3rem', height: '1.5rem' }}
                            />
                            <label className="form-check-label ms-2" htmlFor="upiToggle">
                              {upiEnabled ? (
                                <span className="badge bg-success">Enabled</span>
                              ) : (
                                <span className="badge bg-secondary">Disabled</span>
                              )}
                            </label>
                          </div>
                        </div>
                        
                        {/* UPI Provider Selection - Only shown when UPI is enabled */}
                        {upiEnabled && (
                          <>
                            <p className="text-muted small mb-3">Select your UPI payment provider:</p>
                            <div className="row">
                              {/* Paytm Option */}
                              <div className="col-md-4 mb-3">
                                <div className={`card h-100 ${selectedUpiProvider === 'paytm' ? 'border-success bg-light' : 'border-light'}`} 
                                     style={{ cursor: device?.maintenance_mode ? 'pointer' : 'default', opacity: device?.maintenance_mode ? 1 : 0.7 }}
                                     onClick={() => device?.maintenance_mode && setSelectedUpiProvider('paytm')}>
                                  <div className="card-body text-center p-3">
                                    <div className="mb-2">
                                      <img src="https://cdn.worldvectorlogo.com/logos/paytm-2.svg" 
                                           alt="Paytm" 
                                           style={{ height: '40px', width: 'auto' }} 
                                           onError={(e) => {
                                             e.target.style.display = 'none';
                                             e.target.nextSibling.style.display = 'inline-block';
                                           }} />
                                      <i className="fas fa-mobile-alt" style={{ fontSize: '40px', color: '#00BAF2', display: 'none' }}></i>
                                    </div>
                                    <div className="form-check d-inline-block">
                                      <input
                                        className="form-check-input"
                                        type="radio"
                                        name="upiProvider"
                                        id="paytm"
                                        value="paytm"
                                        checked={selectedUpiProvider === 'paytm'}
                                        onChange={(e) => setSelectedUpiProvider(e.target.value)}
                                        disabled={!device?.maintenance_mode}
                                      />
                                      <label className="form-check-label fw-bold" htmlFor="paytm">
                                        Paytm
                                      </label>
                                    </div>
                                    <div className="small text-success mt-1">
                                      <i className="fas fa-check-circle me-1"></i>
                                      Available
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Amazon Pay Option */}
                              <div className="col-md-4 mb-3">
                                <div className={`card h-100 ${selectedUpiProvider === 'amazon' ? 'border-success bg-light' : 'border-light'}`} 
                                     style={{ cursor: device?.maintenance_mode ? 'pointer' : 'default', opacity: device?.maintenance_mode ? 1 : 0.7 }}
                                     onClick={() => device?.maintenance_mode && setSelectedUpiProvider('amazon')}>
                                  <div className="card-body text-center p-3">
                                    <div className="mb-2">
                                      <img src="https://cdn.worldvectorlogo.com/logos/amazon-pay-1.svg" 
                                           alt="Amazon Pay" 
                                           style={{ height: '40px', width: 'auto' }}
                                           onError={(e) => {
                                             e.target.style.display = 'none';
                                             e.target.nextSibling.style.display = 'inline-block';
                                           }} />
                                      <i className="fab fa-amazon" style={{ fontSize: '40px', color: '#FF9900', display: 'none' }}></i>
                                    </div>
                                    <div className="form-check d-inline-block">
                                      <input
                                        className="form-check-input"
                                        type="radio"
                                        name="upiProvider"
                                        id="amazon"
                                        value="amazon"
                                        checked={selectedUpiProvider === 'amazon'}
                                        onChange={(e) => setSelectedUpiProvider(e.target.value)}
                                        disabled={!device?.maintenance_mode}
                                      />
                                      <label className="form-check-label fw-bold" htmlFor="amazon">
                                        Amazon Pay
                                      </label>
                                    </div>
                                    <div className="small text-success mt-1">
                                      <i className="fas fa-check-circle me-1"></i>
                                      Available
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* PhonePe Option */}
                              <div className="col-md-4 mb-3">
                                <div className="card h-100 border-light" style={{ opacity: 0.6 }}>
                                  <div className="card-body text-center p-3">
                                    <div className="mb-2">
                                      <img src="https://cdn.worldvectorlogo.com/logos/phonepe-1.svg" 
                                           alt="PhonePe" 
                                           style={{ height: '40px', width: 'auto' }}
                                           onError={(e) => {
                                             e.target.style.display = 'none';
                                             e.target.nextSibling.style.display = 'inline-block';
                                           }} />
                                  <i className="fas fa-mobile-alt" style={{ fontSize: '40px', color: '#5F259F', display: 'none' }}></i>
                                </div>
                                <div className="form-check d-inline-block">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="upiProvider"
                                    id="phonepe"
                                    value="phonepe"
                                    disabled
                                  />
                                  <label className="form-check-label fw-bold text-muted" htmlFor="phonepe">
                                    PhonePe
                                  </label>
                                </div>
                                    <div className="small text-warning mt-1">
                                      <i className="fas fa-clock me-1"></i>
                                      Not Available
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Paytm Configuration Form - Only shown when UPI is enabled */}
                      {upiEnabled && selectedUpiProvider === 'paytm' && (
                        <div className="border rounded p-4 bg-light">
                          <h6 className="mb-3 text-primary">
                            <img src="https://cdn.worldvectorlogo.com/logos/paytm-2.svg" 
                                 alt="Paytm" 
                                 style={{ height: '20px', marginRight: '8px' }}
                                 onError={(e) => {
                                   e.target.style.display = 'none';
                                 }} />
                            Paytm Configuration
                          </h6>
                          
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                <i className="fas fa-id-card me-2"></i>
                                Merchant ID (MID) <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${paymentError && !paymentFormData.paytm.MID ? 'is-invalid' : ''}`}
                                value={paymentFormData.paytm.MID}
                                onChange={(e) => handlePaymentFormChange('paytm', 'MID', e.target.value)}
                                placeholder="Enter Merchant ID"
                                disabled={!device?.maintenance_mode}
                              />
                            </div>
                            
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                <i className="fas fa-key me-2"></i>
                                Merchant Key (MID_KEY) <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <input
                                  type={showMidKey ? "text" : "password"}
                                  className={`form-control ${paymentError && !paymentFormData.paytm.MID_KEY ? 'is-invalid' : ''}`}
                                  value={paymentFormData.paytm.MID_KEY}
                                  onChange={(e) => handlePaymentFormChange('paytm', 'MID_KEY', e.target.value)}
                                  placeholder="Enter Merchant Key"
                                  disabled={!device?.maintenance_mode}
                                />
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => setShowMidKey(!showMidKey)}
                                  title={showMidKey ? "Hide key" : "Show key"}
                                >
                                  <i className={`fas ${showMidKey ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {paymentError && (
                            <div className="alert alert-danger">
                              <i className="fas fa-exclamation-circle me-2"></i>
                              {paymentError}
                            </div>
                          )}
                          
                          <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            <strong>Note:</strong> These credentials will be securely stored and used for processing UPI payments through Paytm gateway.
                          </div>
                        </div>
                      )}
                      
                      {/* Amazon Configuration Form */}
                      {upiEnabled && selectedUpiProvider === 'amazon' && (
                        <div className="border rounded p-4 bg-light">
                          <h6 className="mb-3 text-warning">
                            <img src="https://cdn.worldvectorlogo.com/logos/amazon-pay-1.svg" 
                                 alt="Amazon Pay" 
                                 style={{ height: '20px', marginRight: '8px' }}
                                 onError={(e) => {
                                   e.target.style.display = 'none';
                                 }} />
                            Amazon Pay Configuration
                          </h6>
                          
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                <i className="fas fa-key me-2"></i>
                                Secret Key <span className="text-danger">*</span>
                              </label>
                              <input
                                type="password"
                                className={`form-control ${paymentError && !paymentFormData.amazon?.secret_key ? 'is-invalid' : ''}`}
                                value={paymentFormData.amazon?.secret_key || ''}
                                onChange={(e) => handlePaymentFormChange('amazon', 'secret_key', e.target.value)}
                                placeholder="Enter Secret Key"
                                disabled={!device?.maintenance_mode}
                              />
                            </div>
                            
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                <i className="fas fa-unlock-alt me-2"></i>
                                Access Key <span className="text-danger">*</span>
                              </label>
                              <input
                                type="password"
                                className={`form-control ${paymentError && !paymentFormData.amazon?.access_key ? 'is-invalid' : ''}`}
                                value={paymentFormData.amazon?.access_key || ''}
                                onChange={(e) => handlePaymentFormChange('amazon', 'access_key', e.target.value)}
                                placeholder="Enter Access Key"
                                disabled={!device?.maintenance_mode}
                              />
                            </div>
                          </div>
                          
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                <i className="fas fa-id-card me-2"></i>
                                Merchant ID <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${paymentError && !paymentFormData.amazon?.merchantId ? 'is-invalid' : ''}`}
                                value={paymentFormData.amazon?.merchantId || ''}
                                onChange={(e) => handlePaymentFormChange('amazon', 'merchantId', e.target.value)}
                                placeholder="Enter Merchant ID"
                                disabled={!device?.maintenance_mode}
                              />
                            </div>
                            
                            <div className="col-md-6 mb-3">
                              <label className="form-label">
                                <i className="fas fa-store me-2"></i>
                                Store ID <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${paymentError && !paymentFormData.amazon?.storeId ? 'is-invalid' : ''}`}
                                value={paymentFormData.amazon?.storeId || ''}
                                onChange={(e) => handlePaymentFormChange('amazon', 'storeId', e.target.value)}
                                placeholder="Enter Store ID"
                                disabled={!device?.maintenance_mode}
                              />
                            </div>
                          </div>
                          
                          {paymentError && (
                            <div className="alert alert-danger">
                              <i className="fas fa-exclamation-circle me-2"></i>
                              {paymentError}
                            </div>
                          )}
                          
                          <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            <strong>Note:</strong> These credentials will be securely stored and used for processing UPI payments through Amazon Pay gateway.
                          </div>
                        </div>
                      )}
                      
                      {/* Not Available Message for PhonePe */}
                      {upiEnabled && selectedUpiProvider === 'phonepe' && (
                        <div className="alert alert-warning text-center">
                          <i className="fas fa-construction me-2"></i>
                          <strong>PhonePe</strong> integration is not available yet.
                          <br />
                          <small>Please select Paytm or Amazon Pay for now.</small>
                        </div>
                      )}
                      
                      {/* Smart Card Payment Section */}
                      <div className="mt-4 pt-4 border-top">
                        <h6 className="mb-3">
                          <i className="fas fa-id-card me-2"></i>
                          Smart Card Payments
                        </h6>
                        <div className="card border-light">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-credit-card me-2" style={{ fontSize: '24px', color: '#6c757d' }}></i>
                                  <div>
                                    <strong>Smart Card</strong>
                                    <div className="small text-muted">Enable smart card payments for this device</div>
                                  </div>
                                </div>
                              </div>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="smartCardToggle"
                                  checked={smartCardEnabled}
                                  onChange={(e) => setSmartCardEnabled(e.target.checked)}
                                  disabled={!device?.maintenance_mode}
                                  style={{ width: '3rem', height: '1.5rem' }}
                                />
                                <label className="form-check-label ms-2" htmlFor="smartCardToggle">
                                  {smartCardEnabled ? (
                                    <span className="badge bg-success">Enabled</span>
                                  ) : (
                                    <span className="badge bg-secondary">Disabled</span>
                                  )}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Pluxee/Sodexo Payment Section */}
                      <div className="mt-4 pt-4 border-top">
                        <h6 className="mb-3">
                          <i className="fas fa-wallet me-2"></i>
                          Pluxee / Sodexo
                        </h6>
                        <div className="card border-light">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-wallet me-2" style={{ fontSize: '24px', color: '#e85d04' }}></i>
                                  <div>
                                    <strong>Pluxee / Sodexo</strong>
                                    <div className="small text-muted">Enable Pluxee/Sodexo prepaid wallet payments</div>
                                  </div>
                                </div>
                              </div>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="pluxeeToggle"
                                  checked={pluxeeEnabled}
                                  onChange={(e) => setPluxeeEnabled(e.target.checked)}
                                  disabled={!device?.maintenance_mode}
                                  style={{ width: '3rem', height: '1.5rem' }}
                                />
                                <label className="form-check-label ms-2" htmlFor="pluxeeToggle">
                                  {pluxeeEnabled ? (
                                    <span className="badge bg-success">Enabled</span>
                                  ) : (
                                    <span className="badge bg-secondary">Disabled</span>
                                  )}
                                </label>
                              </div>
                            </div>

                            {pluxeeEnabled && (
                              <div className="mt-3 pt-3 border-top">
                                {userClaims?.sadmin && (
                                  <div className="mb-3">
                                    <label className="form-label">
                                      <i className="fas fa-key me-1 text-warning"></i>
                                      API Key
                                      <span className="badge bg-danger ms-2" style={{ fontSize: '0.65rem' }}>Super Admin Only</span>
                                    </label>
                                    <input
                                      type="password"
                                      className="form-control"
                                      value={pluxeeFormData.apiKey}
                                      onChange={(e) => setPluxeeFormData(prev => ({ ...prev, apiKey: e.target.value }))}
                                      placeholder="Enter Pluxee API Key"
                                      disabled={!device?.maintenance_mode}
                                    />
                                  </div>
                                )}
                                <div className="mb-3">
                                  <label className="form-label">
                                    <i className="fas fa-fingerprint me-1 text-info"></i>
                                    AID
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={pluxeeFormData.aid}
                                    onChange={(e) => setPluxeeFormData(prev => ({ ...prev, aid: e.target.value }))}
                                    placeholder="Enter AID"
                                    disabled={!device?.maintenance_mode}
                                  />
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">
                                    <i className="fas fa-store me-1 text-primary"></i>
                                    MID
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={pluxeeFormData.mid}
                                    onChange={(e) => setPluxeeFormData(prev => ({ ...prev, mid: e.target.value }))}
                                    placeholder="Enter MID"
                                    disabled={!device?.maintenance_mode}
                                  />
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">
                                    <i className="fas fa-terminal me-1 text-success"></i>
                                    TID
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={pluxeeFormData.tid}
                                    onChange={(e) => setPluxeeFormData(prev => ({ ...prev, tid: e.target.value }))}
                                    placeholder="Enter TID"
                                    disabled={!device?.maintenance_mode}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentConfig(false)}>
                        Cancel
                      </button>
                      <button 
                        type="button" 
                        className="btn btn-success"
                        onClick={handlePaymentConfigSave}
                        disabled={!device?.maintenance_mode || saving || 
                          (!upiEnabled && !smartCardEnabled && !pluxeeEnabled) ||
                          (upiEnabled && selectedUpiProvider === 'paytm' && (!paymentFormData.paytm?.MID || !paymentFormData.paytm?.MID_KEY)) ||
                          (upiEnabled && selectedUpiProvider === 'amazon' && (!paymentFormData.amazon?.secret_key || !paymentFormData.amazon?.access_key || !paymentFormData.amazon?.merchantId || !paymentFormData.amazon?.storeId)) ||
                          (upiEnabled && selectedUpiProvider === 'phonepe')
                        }
                      >
                        {saving ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-2"></i>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-2"></i>
                            Save Configuration
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Customer Care Configuration Modal */}
            {showCustomerCare && (
              <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                <div className="modal-dialog">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        <i className="fas fa-headset me-2"></i>
                        Customer Care Settings
                      </h5>
                      <button type="button" className="btn-close" onClick={() => setShowCustomerCare(false)}></button>
                    </div>
                    <div className="modal-body">
                      <p className="text-muted small mb-4">
                        Configure customer support contact details displayed on the vending machine touch screen.
                      </p>
                      
                      <div className="mb-3">
                        <label className="form-label">
                          <i className="fas fa-phone me-2 text-primary"></i>
                          Phone Numbers
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={customerCareData.phone}
                          onChange={(e) => setCustomerCareData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="e.g., 09884416950 / 9962557790"
                        />
                        <small className="text-muted">Multiple numbers can be separated with /</small>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          <i className="fas fa-envelope me-2 text-danger"></i>
                          Email Address
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={customerCareData.email}
                          onChange={(e) => setCustomerCareData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="e.g., support@example.com"
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          <i className="fab fa-whatsapp me-2 text-success"></i>
                          WhatsApp Chat Number
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={customerCareData.chat}
                          onChange={(e) => setCustomerCareData(prev => ({ ...prev, chat: e.target.value }))}
                          placeholder="e.g., 09884416950"
                        />
                        <small className="text-muted">Used to generate WhatsApp QR code on touch screen</small>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          <i className="fas fa-globe me-2 text-info"></i>
                          Website / Feedback Portal
                          <span className="badge bg-secondary ms-2">Optional</span>
                        </label>
                        <input
                          type="url"
                          className="form-control"
                          value={customerCareData.website}
                          onChange={(e) => setCustomerCareData(prev => ({ ...prev, website: e.target.value }))}
                          placeholder="e.g., https://feedback.yourcompany.com"
                        />
                        <small className="text-muted">
                          If set, touch screen will show QR linking to this website instead of WhatsApp
                        </small>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={() => setShowCustomerCare(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSaveCustomerCare}
                        disabled={customerCareSaving}
                      >
                        {customerCareSaving ? (
                          <>
                            <i className="fas fa-spinner fa-spin me-2"></i>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-2"></i>
                            Save Settings
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Layout Editor Modal */}
            {showLayoutEditor && (
              <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                <div className="modal-dialog modal-xl">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        {device?.maintenance_mode ? 'Edit Layout' : 'View Layout'}: {device?.id}
                        {!device?.maintenance_mode && (
                          <span className="badge bg-info ms-2">Read-Only</span>
                        )}
                      </h5>
                      <button type="button" className="btn-close" onClick={() => setShowLayoutEditor(false)}></button>
                    </div>
                    <div className="modal-body">
                      {/* Layout Mode Selection - Only for maintenance mode */}
                      {device?.maintenance_mode && (
                        <div className="mb-4">
                          <div className="card bg-light">
                            <div className="card-body">
                              <h6 className="card-title mb-3">
                                <i className="fas fa-cog me-2"></i>
                                Layout Display Mode
                              </h6>
                              <div className="row">
                                <div className="col-md-6">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="layout_display_mode"
                                      id="layout_mode_image_edit"
                                      value="image"
                                      checked={formData.layout_mode === 'image'}
                                      onChange={(e) => handleInputChange('layout_mode', e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor="layout_mode_image_edit">
                                      <i className="fas fa-image me-2 text-success"></i>
                                      <strong>Image Mode</strong>
                                      <div className="small text-muted">Show product images and names in layout slots</div>
                                    </label>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="form-check">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name="layout_display_mode"
                                      id="layout_mode_slot_edit"
                                      value="slot"
                                      checked={formData.layout_mode === 'slot'}
                                      onChange={(e) => handleInputChange('layout_mode', e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor="layout_mode_slot_edit">
                                      <i className="fas fa-th-large me-2 text-primary"></i>
                                      <strong>Slot Mode</strong>
                                      <div className="small text-muted">Show slot numbers only for simpler view</div>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6>Physical Layout Matrix ({layoutRows} rows, flexible columns)</h6>
                          {device?.maintenance_mode && (
                            <div className="btn-group btn-group-sm">
                              <button className="btn btn-outline-primary" onClick={addRow} title="Add Row">
                                <i className="fas fa-plus me-1"></i>Row
                              </button>
                              <button className="btn btn-outline-danger" onClick={removeRow} disabled={layoutRows <= 1} title="Remove Row">
                                <i className="fas fa-minus me-1"></i>Row
                              </button>
                              <button className="btn btn-outline-secondary" onClick={addColumn} title="Add Column to All Rows">
                                <i className="fas fa-plus me-1"></i>All Cols
                              </button>
                              <button className="btn btn-outline-secondary" onClick={removeColumn} disabled={layoutCols <= 1} title="Remove Column from All Rows">
                                <i className="fas fa-minus me-1"></i>All Cols
                              </button>
                            </div>
                          )}
                        </div>
                        <div className={`alert ${device?.maintenance_mode ? 'alert-info' : 'alert-warning'}`}>
                          <i className="fas fa-info-circle me-2"></i>
                          {device?.maintenance_mode ? (
                            <>Click on any slot to edit its contents. Each row can have different numbers of columns. Use the + / - buttons next to each row to add/remove columns for that specific row.</>
                          ) : (
                            <>This is a read-only view of the layout. To edit slots and layout configuration, enable maintenance mode first.</>
                          )}
                        </div>
                        {/* Slot Status Summary */}
                        {(() => {
                          const slots = tempLayoutData
                          const totalSlots = Object.keys(slots).length
                          const lockedOutSlots = Object.values(slots).filter(slot => 
                            slot.slot_lockout === true || slot.slock_lockout === true
                          ).length
                          const functionalSlots = totalSlots - lockedOutSlots
                          const expiredSlots = expiryTrackingEnabled ? Object.values(slots).filter(slot => isSlotExpired(slot)).length : 0
                          
                          return (
                            <div className="row mb-3">
                              <div className={expiryTrackingEnabled ? 'col-md-3' : 'col-md-4'}>
                                <div className="card bg-light border-0 h-100">
                                  <div className="card-body text-center py-2">
                                    <div className="fw-bold text-primary">{totalSlots}</div>
                                    <small className="text-muted">Total Slots</small>
                                  </div>
                                </div>
                              </div>
                              <div className={expiryTrackingEnabled ? 'col-md-3' : 'col-md-4'}>
                                <div className="card bg-light border-0 h-100">
                                  <div className="card-body text-center py-2">
                                    <div className="fw-bold text-success">{functionalSlots}</div>
                                    <small className="text-muted">Functional</small>
                                  </div>
                                </div>
                              </div>
                              <div className={expiryTrackingEnabled ? 'col-md-3' : 'col-md-4'}>
                                <div className="card bg-light border-0 h-100">
                                  <div className="card-body text-center py-2">
                                    <div className="fw-bold text-danger">{lockedOutSlots}</div>
                                    <small className="text-muted">Locked Out</small>
                                  </div>
                                </div>
                              </div>
                              {expiryTrackingEnabled && (
                                <div className="col-md-3">
                                  <div className="card bg-light border-0 h-100">
                                    <div className="card-body text-center py-2">
                                      <div className="fw-bold text-warning">{expiredSlots}</div>
                                      <small className="text-muted">Expired</small>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        })()}

                        {/* Maintenance Controls - Only in maintenance mode */}
                        {device?.maintenance_mode && (
                          <div className="mb-3">
                            <div className="card border-warning">
                              <div className="card-header bg-warning bg-opacity-10">
                                <h6 className="mb-0">
                                  <i className="fas fa-tools me-2"></i>
                                  Maintenance Operations
                                </h6>
                              </div>
                              <div className="card-body">
                                <div className="row">
                                  <div className="col-md-6 mb-2">
                                    <button
                                      className="btn btn-outline-danger w-100"
                                      onClick={clearAllSlotLockouts}
                                      disabled={saving}
                                      title="Clear lockout status from all slots to make them functional"
                                    >
                                      <i className="fas fa-unlock me-2"></i>
                                      Clear All Lockouts
                                    </button>
                                  </div>
                                  <div className="col-md-6 mb-2">
                                    <button
                                      className="btn btn-outline-success w-100"
                                      onClick={restockAllSlots}
                                      disabled={saving}
                                      title="Restock all functional slots to their full capacity"
                                    >
                                      <i className="fas fa-boxes me-2"></i>
                                      Restock All Slots
                                    </button>
                                  </div>
                                </div>
                                <div className="small text-muted mt-2">
                                  <i className="fas fa-exclamation-triangle me-1"></i>
                                  <strong>Warning:</strong> These operations affect multiple slots. Locked-out slots are automatically skipped during restock operations.
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Legend */}
                        <div className="mb-3">
                          <div className="card bg-light border-0">
                            <div className="card-body py-2">
                              <div className="row text-center">
                                <div className="col-md-3">
                                  <div className="d-flex align-items-center justify-content-center">
                                    <div className="border" style={{ width: '20px', height: '20px', backgroundColor: '#f8f9fa' }}></div>
                                    <small className="ms-2">Empty Slot</small>
                                  </div>
                                </div>
                                <div className="col-md-3">
                                  <div className="d-flex align-items-center justify-content-center">
                                    <div className="border" style={{ width: '20px', height: '20px', backgroundColor: '#e3f2fd' }}></div>
                                    <small className="ms-2">Product Slot</small>
                                  </div>
                                </div>
                                <div className="col-md-3">
                                  <div className="d-flex align-items-center justify-content-center">
                                    <div className="border border-danger" style={{ width: '20px', height: '20px', backgroundColor: 'rgba(220, 53, 69, 0.1)', borderWidth: '2px !important' }}></div>
                                    <small className="ms-2">Locked Out</small>
                                  </div>
                                </div>
                                <div className="col-md-3">
                                  <div className="d-flex align-items-center justify-content-center">
                                    <i className="fas fa-lock text-danger me-1"></i>
                                    <small>Non-functional</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="border p-3 bg-light overflow-auto" style={{ maxHeight: '500px' }}>
                          {renderLayoutMatrix()}
                        </div>
                      </div>
                      
                      {/* Slot Edit Modal */}
                      {editingSlot && (
                        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
                          <div className="modal-dialog">
                            <div className="modal-content">
                              <div className="modal-header">
                                <h5 className="modal-title">
                                  Edit Slot - Position ({editingSlot.row + 1}, {editingSlot.col + 1})
                                </h5>
                                <button 
                                  type="button" 
                                  className="btn-close" 
                                  onClick={() => {
                                    setEditingSlot(null)
                                    setImageUrlError('')
                                  }}
                                ></button>
                              </div>
                              <div className="modal-body">
                                <div className="row">
                                  <div className="col-md-6">
                                    <div className="mb-3">
                                      <label className="form-label">Slot Number</label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={slotFormData.slotNumber}
                                        onChange={(e) => setSlotFormData({...slotFormData, slotNumber: e.target.value})}
                                        placeholder="e.g., 1A, 2B, etc."
                                      />
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="mb-3">
                                      <label className="form-label">Product Name</label>
                                      <ProductSearchInput
                                        placeholder="Search products or enter manually..."
                                        value={slotFormData.name}
                                        onChange={(value) => setSlotFormData({...slotFormData, name: value})}
                                        onProductSelect={(product) => {
                                          const newImageUrl = product.imageUrl || slotFormData.imageUrl
                                          setSlotFormData({
                                            ...slotFormData,
                                            name: product.displayname,
                                            price: product.MRP?.toString() || slotFormData.price,
                                            imageUrl: newImageUrl
                                          })
                                          // Validate the imageUrl from product
                                          const validation = validateImageUrl(newImageUrl)
                                          setImageUrlError(validation.error)
                                        }}
                                      />
                                      <div className="small text-muted mt-1">
                                        <i className="fas fa-lightbulb me-1"></i>
                                        Search from product master list or enter manually
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">Product Image URL</label>
                                  <input
                                    type="url"
                                    className={`form-control ${imageUrlError ? 'is-invalid' : ''}`}
                                    value={slotFormData.imageUrl}
                                    onChange={(e) => {
                                      const newUrl = e.target.value
                                      setSlotFormData({...slotFormData, imageUrl: newUrl})
                                      const validation = validateImageUrl(newUrl)
                                      setImageUrlError(validation.error)
                                    }}
                                    placeholder="https://example.com/product-image.jpg"
                                  />
                                  {imageUrlError && (
                                    <div className="invalid-feedback d-block">
                                      <i className="fas fa-exclamation-triangle me-1"></i>
                                      {imageUrlError}
                                    </div>
                                  )}
                                  <small className="text-muted">
                                    Only HTTP/HTTPS URLs are accepted. Data URIs are not allowed.
                                  </small>
                                  {slotFormData.imageUrl && !imageUrlError && (
                                    <div className="mt-2">
                                      <img 
                                        src={slotFormData.imageUrl} 
                                        alt="Product preview"
                                        style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                        className="rounded border"
                                        onError={(e) => {
                                          e.target.style.display = 'none'
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className="row">
                                  <div className="col-md-4">
                                    <div className="mb-3">
                                      <label className="form-label">Price (₹)</label>
                                      <input
                                        type="number"
                                        className="form-control"
                                        value={slotFormData.price}
                                        onChange={(e) => setSlotFormData({...slotFormData, price: e.target.value})}
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                      />
                                    </div>
                                  </div>
                                  <div className="col-md-4">
                                    <div className="mb-3">
                                      <label className="form-label">Current Quantity</label>
                                      <input
                                        type="number"
                                        className={`form-control ${
                                          slotFormData.capacity && slotFormData.quantity && 
                                          parseInt(slotFormData.quantity) > parseInt(slotFormData.capacity) 
                                            ? 'is-invalid' : ''
                                        }`}
                                        value={slotFormData.quantity}
                                        onChange={(e) => setSlotFormData({...slotFormData, quantity: e.target.value})}
                                        placeholder="0"
                                        min="0"
                                      />
                                      {slotFormData.capacity && slotFormData.quantity && 
                                       parseInt(slotFormData.quantity) > parseInt(slotFormData.capacity) && (
                                        <div className="invalid-feedback d-block">
                                          Quantity cannot exceed max capacity ({slotFormData.capacity})
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="col-md-4">
                                    <div className="mb-3">
                                      <label className="form-label">Max Capacity</label>
                                      <input
                                        type="number"
                                        className="form-control"
                                        value={slotFormData.capacity}
                                        onChange={(e) => {
                                          const newCapacity = e.target.value
                                          setSlotFormData({...slotFormData, capacity: newCapacity})
                                        }}
                                        placeholder="0"
                                        min="0"
                                      />
                                    </div>
                                  </div>
                                </div>
                                {slotFormData.quantity && slotFormData.capacity && (
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <small className="fw-bold">Stock Level: {Math.round((parseInt(slotFormData.quantity) || 0) / (parseInt(slotFormData.capacity) || 1) * 100)}%</small>
                                      <small className="text-muted">{slotFormData.quantity}/{slotFormData.capacity}</small>
                                    </div>
                                    <div className="progress" style={{ height: '6px' }}>
                                      <div 
                                        className={`progress-bar ${
                                          ((parseInt(slotFormData.quantity) || 0) / (parseInt(slotFormData.capacity) || 1) * 100) >= 80 ? 'bg-success' :
                                          ((parseInt(slotFormData.quantity) || 0) / (parseInt(slotFormData.capacity) || 1) * 100) >= 50 ? 'bg-warning' : 'bg-danger'
                                        }`}
                                        style={{ width: `${Math.max((parseInt(slotFormData.quantity) || 0) / (parseInt(slotFormData.capacity) || 1) * 100, 2)}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                )}
                                {expiryTrackingEnabled && (
                                  <div className="mb-3">
                                    <label className="form-label">
                                      <i className="fas fa-calendar-times me-1 text-warning"></i>
                                      Expiry Date
                                    </label>
                                    <input
                                      type="date"
                                      className={`form-control ${slotFormData.expiry && isSlotExpired({ expiry: slotFormData.expiry }) ? 'is-invalid' : ''}`}
                                      value={slotFormData.expiry}
                                      onChange={(e) => setSlotFormData({...slotFormData, expiry: e.target.value})}
                                    />
                                    {slotFormData.expiry && isSlotExpired({ expiry: slotFormData.expiry }) && (
                                      <div className="invalid-feedback d-block">
                                        <i className="fas fa-exclamation-triangle me-1"></i>
                                        This product has expired!
                                      </div>
                                    )}
                                  </div>
                                )}
                                {(() => {
                                  const currentSlot = editingSlot.slotId ? tempLayoutData[editingSlot.slotId] : null
                                  const isLockedOut = currentSlot && (currentSlot.slot_lockout === true || currentSlot.slock_lockout === true)
                                  
                                  return (
                                    <>
                                      {isLockedOut && (
                                        <div className="alert alert-danger">
                                          <div className="d-flex align-items-center">
                                            <i className="fas fa-lock me-2"></i>
                                            <div>
                                              <strong>This slot is currently locked out (non-functional)</strong>
                                              <div className="small">Slot lockout status is managed by the device and cannot be changed from the portal.</div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      <div className="alert alert-light">
                                        <small className="text-muted">
                                          <strong>Position:</strong> Row {editingSlot.row + 1}, Column {editingSlot.col + 1}
                                          <br />
                                          <strong>Default Slot Number:</strong> {getDefaultSlotNumber(editingSlot.row, editingSlot.col)}
                                          {isLockedOut && (
                                            <>
                                              <br />
                                              <strong>Status:</strong> <span className="text-danger">🔒 Locked Out</span>
                                            </>
                                          )}
                                        </small>
                                      </div>
                                    </>
                                  )
                                })()}
                              </div>
                              <div className="modal-footer">
                                <button 
                                  type="button" 
                                  className="btn btn-secondary" 
                                  onClick={() => {
                                    setEditingSlot(null)
                                    setImageUrlError('')
                                  }}
                                >
                                  Cancel
                                </button>
                                <button 
                                  type="button" 
                                  className="btn btn-primary"
                                  onClick={handleSlotSave}
                                  disabled={
                                    !slotFormData.name.trim() || 
                                    imageUrlError || 
                                    (slotFormData.capacity && slotFormData.quantity && 
                                     parseInt(slotFormData.quantity) > parseInt(slotFormData.capacity))
                                  }
                                >
                                  <i className="fas fa-save me-2"></i>
                                  Save Product
                                </button>
                                {editingSlot.slotId && (
                                  <button 
                                    type="button" 
                                    className="btn btn-danger"
                                    onClick={handleSlotDelete}
                                  >
                                    <i className="fas fa-trash me-2"></i>
                                    Remove Product
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowLayoutEditor(false)}>
                        {device?.maintenance_mode ? 'Cancel' : 'Close'}
                      </button>
                      {device?.maintenance_mode && (
                        <button 
                          type="button" 
                          className="btn btn-primary"
                          onClick={handleLayoutSave}
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <i className="fas fa-spinner fa-spin me-2"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save me-2"></i>
                              Save Layout
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Status Overview */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card bg-light">
                  <div className="card-body">
                    <div className="row text-center">
                      <div className="col-md-3">
                        <div className="h5 mb-1">
                          <span className={`badge ${
                            device?.status === 'online' ? 'bg-success' :
                            device?.status === 'offline' ? 'bg-danger' :
                            device?.status === 'maintenance' ? 'bg-warning text-dark' :
                            'bg-secondary'
                          }`}>
                            {device?.status || 'Unknown'}
                          </span>
                        </div>
                        <div className="small text-muted">Status</div>
                      </div>
                      <div className="col-md-3">
                        <div className="h5 mb-1">
                          <span className={`badge ${
                            device?.maintenance_mode ? 'bg-warning text-dark' : 'bg-success'
                          }`}>
                            {device?.maintenance_mode ? 'ON' : 'OFF'}
                          </span>
                        </div>
                        <div className="small text-muted">Maintenance Mode</div>
                      </div>
                      <div className="col-md-3">
                        <div className="h5 mb-1">
                          <i className="fas fa-key text-info"></i>
                        </div>
                        <div className="small text-muted">Service PIN</div>
                        <div className="fw-bold small">
                          {device?.['service-pin'] ? '****' + device['service-pin'].slice(-2) : 'Not Set'}
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="h5 mb-1 text-primary">
                          {(device?.rows || 0) * (device?.cols || 0)}
                        </div>
                        <div className="small text-muted">Total Slots</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Actions - Only for Super Admin */}
            {userClaims?.sadmin && (
              <div className="row mb-3">
                <div className="col">
                  <div className="btn-group">
                    <button
                      className={`btn ${editMode ? 'btn-secondary' : 'btn-primary'}`}
                      onClick={() => setEditMode(!editMode)}
                      disabled={saving}
                    >
                      <i className={`fas ${editMode ? 'fa-eye' : 'fa-edit'} me-1`}></i>
                      {editMode ? 'View Mode' : 'Edit Mode'}
                    </button>
                    {editMode && (
                      <button
                        className="btn btn-success"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-1"></i>
                            Save Changes
                          </>
                        )}
                      </button>
                    )}
                    <button
                      className="btn btn-danger"
                      onClick={handleDelete}
                      disabled={saving}
                    >
                      <i className="fas fa-trash me-1"></i>
                      Delete
                    </button>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">
                      <i className="fas fa-shield-alt me-1"></i>
                      Super Admin privileges required for device editing and deletion
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Non-Admin View Notice */}
            {!userClaims?.sadmin && (
              <div className="row mb-3">
                <div className="col">
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    <strong>Read-Only Access:</strong> Device configuration, layout editing, and deletion require Super Admin privileges.
                  </div>
                </div>
              </div>
            )}

            {/* Super Admin Only: Advanced Configuration Details */}
            {userClaims?.sadmin && (
              <>
                <div className="row mb-3">
                  <div className="col">
                    <div className="alert alert-warning">
                      <i className="fas fa-shield-alt me-2"></i>
                      <strong>Super Admin View:</strong> The following section shows advanced configuration details for technical management. This view is restricted to Super Admin users only.
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-4">
                    <h6>Device Information</h6>
                    {editMode ? (
                      <div>
                        {Object.entries(device || {})
                          .filter(([key]) => !key.startsWith('product_') && !['rows', 'cols', 'row_configs'].includes(key))
                          .map(([key, value]) => renderDeviceField(key, value))}
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <tbody>
                            {Object.entries(device || {})
                              .filter(([key]) => !key.startsWith('product_') && !['rows', 'cols', 'row_configs'].includes(key))
                              .map(([key, value]) => renderDeviceField(key, value))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  
                  <div className="col-md-4">
                    <h6>Layout Configuration</h6>
                    {editMode ? (
                      <div>
                        {Object.entries(device || {})
                          .filter(([key]) => ['rows', 'cols', 'row_configs'].includes(key))
                          .map(([key, value]) => renderDeviceField(key, value))}
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <tbody>
                            {Object.entries(device || {})
                              .filter(([key]) => ['rows', 'cols', 'row_configs'].includes(key))
                              .map(([key, value]) => renderDeviceField(key, value))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    
                    {/* Layout Summary */}
                    {device && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <small className="text-muted">Layout Summary</small>
                        <div className="mt-1">
                          <strong>Grid:</strong> {device.rows || 0} rows × {device.cols || 0} columns
                        </div>
                        <div>
                          <strong>Total Slots:</strong> {(device.rows || 0) * (device.cols || 0)}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="col-md-4">
                    <h6>Products ({Object.keys(device || {}).filter(key => key.startsWith('product_')).length})</h6>
                    {editMode ? (
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {Object.entries(device || {})
                          .filter(([key]) => key.startsWith('product_'))
                          .sort(([a], [b]) => {
                            const numA = parseInt(a.split('_')[1])
                            const numB = parseInt(b.split('_')[1])
                            return numA - numB
                          })
                          .map(([key, value]) => renderDeviceField(key, value))}
                      </div>
                    ) : (
                      <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table className="table table-sm">
                          <tbody>
                            {Object.entries(device || {})
                              .filter(([key]) => key.startsWith('product_'))
                              .sort(([a], [b]) => {
                                const numA = parseInt(a.split('_')[1])
                                const numB = parseInt(b.split('_')[1])
                                return numA - numB
                              })
                              .map(([key, value]) => renderDeviceField(key, value))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Normal User View: Basic Device Summary */}
            {!userClaims?.sadmin && (
              <div className="row">
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="card-title mb-0">
                        <i className="fas fa-info-circle me-2"></i>
                        Device Summary
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-6">
                          <small className="text-muted">Device ID</small>
                          <div className="fw-semibold">{device?.id || 'N/A'}</div>
                        </div>
                        <div className="col-6">
                          <small className="text-muted">Machine ID</small>
                          <div className="fw-semibold">{device?.machine_id || 'N/A'}</div>
                        </div>
                        <div className="col-6 mt-3">
                          <small className="text-muted">Currency</small>
                          <div>
                            <span className="badge bg-info">
                              {currencyOptions.find(c => c.code === device?.currency)?.name || device?.currency || 'USD'}
                            </span>
                          </div>
                        </div>
                        <div className="col-6 mt-3">
                          <small className="text-muted">Layout Mode</small>
                          <div>
                            <span className={`badge ${device?.layout_mode === 'image' ? 'bg-success' : 'bg-primary'}`}>
                              <i className={`fas fa-${device?.layout_mode === 'image' ? 'image' : 'th-large'} me-1`}></i>
                              {device?.layout_mode === 'image' ? 'Image Mode' : 'Slot Mode'}
                            </span>
                          </div>
                        </div>
                        <div className="col-6 mt-3">
                          <small className="text-muted">Maintenance Mode</small>
                          <div>
                            <span className={`badge ${device?.maintenance_mode ? 'bg-warning text-dark' : 'bg-success'}`}>
                              <i className={`fas fa-${device?.maintenance_mode ? 'wrench' : 'check'} me-1`}></i>
                              {device?.maintenance_mode ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                        </div>
                        <div className="col-6 mt-3">
                          <small className="text-muted">Product Count</small>
                          <div className="fw-semibold">{device?.product_count || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="card-title mb-0">
                        <i className="fas fa-th-large me-2"></i>
                        Layout Information
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-4">
                          <small className="text-muted">Rows</small>
                          <div className="fw-semibold fs-4 text-primary">{device?.rows || 0}</div>
                        </div>
                        <div className="col-4">
                          <small className="text-muted">Columns</small>
                          <div className="fw-semibold fs-4 text-primary">{device?.cols || 0}</div>
                        </div>
                        <div className="col-4">
                          <small className="text-muted">Total Slots</small>
                          <div className="fw-semibold fs-4 text-success">{(device?.rows || 0) * (device?.cols || 0)}</div>
                        </div>
                      </div>
                      
                      {device?.customer_care && (
                        <div className="mt-3 pt-3 border-top">
                          <small className="text-muted">Customer Support</small>
                          <div className="mt-1">
                            {device.customer_care.email && (
                              <div>
                                <i className="fas fa-envelope me-2 text-primary"></i>
                                <small>{device.customer_care.email}</small>
                              </div>
                            )}
                            {device.customer_care.phone && (
                              <div>
                                <i className="fas fa-phone me-2 text-success"></i>
                                <small>{device.customer_care.phone}</small>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* QR Code Generation Modal */}
      {showQRModal && qrData && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-qrcode me-2"></i>
                  Replenishment QR Code - {qrData.machineName}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowQRModal(false)}></button>
              </div>
              <div className="modal-body text-center">
                <div className="row">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-body">
                        <h6 className="card-title">QR Code</h6>
                        <img 
                          src={qrData.qrCodeDataUrl} 
                          alt="Replenishment QR Code"
                          className="img-fluid mb-3"
                          style={{ maxWidth: '250px' }}
                        />
                        <div className="d-grid gap-2">
                          <button 
                            className="btn btn-primary"
                            onClick={handleDownloadQR}
                          >
                            <i className="fas fa-download me-2"></i>
                            Download QR Code
                          </button>
                          <button 
                            className="btn btn-outline-secondary"
                            onClick={handleCopyReplenishUrl}
                          >
                            <i className="fas fa-copy me-2"></i>
                            Copy Link
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-body text-start">
                        <h6 className="card-title">Instructions</h6>
                        <div className="small">
                          <div className="mb-3">
                            <strong>For Replenishment Workers:</strong>
                            <ol className="mt-2 ps-3">
                              <li>Print this QR code and attach it to the machine</li>
                              <li>Scan the QR code with your mobile device</li>
                              <li>Log in with your replenishment credentials</li>
                              <li>Access the mobile-optimized layout editor</li>
                              <li>Update product quantities and information</li>
                            </ol>
                          </div>
                          
                          <div className="mb-3">
                            <strong>Session Details:</strong>
                            <ul className="mt-2 ps-3">
                              <li>30-minute time limit per session</li>
                              <li>Machine-specific access only</li>
                              <li>Automatic logout on expiration</li>
                            </ul>
                          </div>
                          
                          <div className="bg-light p-2 rounded">
                            <small>
                              <strong>Machine:</strong> {qrData.machineId}<br />
                              <strong>Location:</strong> {qrData.location}<br />
                              <strong>Generated:</strong> {new Date(qrData.generatedAt).toLocaleString()}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowQRModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}